import { useState, useRef, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import { Camera, BarChart2, ClipboardList, FolderOpen, TrendingUp, DollarSign, Lightbulb, MessageSquare, Mail, Share2 } from "lucide-react";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

const STRIPE_PRICES = {
  pro: "price_1TALbeA3L6tBLy1o3x7hGz3D",
};

const PLANS = {
  free:         { name: "UnitScout+ Trial", price: "Free",  scans: Infinity },
  pro:          { name: "UnitScout+",       price: "$5.99", scans: Infinity },
  casual:       { name: "UnitScout+",       price: "$5.99", scans: Infinity },
  regular:      { name: "UnitScout+",       price: "$5.99", scans: Infinity },
  professional: { name: "UnitScout+",       price: "$5.99", scans: Infinity },
};

const CATEGORIES = [
  "Electronics", "Furniture", "Tools & Hardware",
  "Clothing & Soft Goods", "Collectibles",
  "Boxes/Unknown", "Miscellaneous", "Mixed",
];
// Sub-categories available when Mixed is selected (all except Mixed itself)
const MIXED_CATEGORIES = [
  "Electronics", "Furniture", "Tools & Hardware",
  "Clothing & Soft Goods", "Collectibles", "Boxes/Unknown", "Miscellaneous",
];

const C = {
  bg: "#0F1117", bgCard: "#1E2130", bgDeep: "#13161F",
  gold: "#F5A623", white: "#FFFFFF", grey: "#A0AEC0",
  border: "#2D3748", green: "#48BB78", red: "#FC8181",
  yellow: "#EAB308", blue: "#63B3ED",
};

const OUTCOME_COLORS = {
  won: C.green, lost: C.red, passed: "#9CA3AF", still_bidding: C.yellow,
};
const OUTCOME_LABELS = {
  won: "🟢 Won", lost: "🔴 Lost", passed: "⚪ Passed", still_bidding: "🟡 Watching",
};

const S = {
  page: {
    fontFamily: "'Georgia', 'Times New Roman', serif",
    background: C.bg, minHeight: "100vh", width: "100%",
    color: C.white, margin: 0, padding: 0, overflowX: "hidden",
  },
  goldBtn: {
    background: C.gold, color: C.bg, border: "none",
    borderRadius: 8, padding: "14px 32px", fontSize: 16, fontWeight: 700, cursor: "pointer",
  },
  outlineBtn: {
    background: "transparent", color: C.gold, border: `2px solid ${C.gold}`,
    borderRadius: 8, padding: "10px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer",
  },
  card: { background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 },
  input: {
    width: "100%", background: C.bgCard, border: `1px solid ${C.border}`,
    borderRadius: 8, padding: "12px 16px", color: C.white,
    fontSize: 15, boxSizing: "border-box", outline: "none",
  },
  sectionTitle: {
    fontFamily: "'Georgia', serif", fontStyle: "italic", fontSize: 42,
    fontWeight: 400, textAlign: "center", color: C.white, margin: "0 0 16px 0",
  },
  redBtn: {
    background: "transparent", color: "#FC8181", border: "1px solid #FC8181",
    borderRadius: 8, padding: "10px 20px", fontSize: 13, cursor: "pointer",
  },
};

// -- Category prompt - AI assigns one category only ---------------------------
const CATEGORY_PROMPT = `Look at this storage unit photo. Assign exactly one category based on what dominates the visible space.

Categories:
- Electronics
- Furniture
- Tools & Hardware
- Clothing & Soft Goods
- Collectibles
- Boxes/Unknown
- Miscellaneous
- Mixed

Rules:
- Choose the category that fills the most visible space
- Boxes/Unknown when mostly boxes with no clear contents visible
- Mixed when clearly 2+ distinct categories present
- When uncertain, choose Miscellaneous
- Respond with ONLY the category name. Nothing else.`;

// -- Marketplace URL builders --------------------------------------------------
const buildEbayUrl = () => `https://www.ebay.com/sch/i.html?LH_Sold=1&LH_Complete=1&_sop=13`;
const buildFbUrl = () => `https://www.facebook.com/marketplace/`;
const buildOfferUpUrl = () => `https://offerup.com/`;

const injectFonts = () => {
  document.body.style.margin = "0";
  document.body.style.padding = "0";
  document.body.style.background = "#0F1117";
  if (!document.getElementById("unitscout-fonts")) {
    const link = document.createElement("link");
    link.id = "unitscout-fonts";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&display=swap";
    document.head.appendChild(link);
  }
  if (!document.getElementById("google-maps-script")) {
    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}&libraries=places`;
    script.async = true;
    document.head.appendChild(script);
  }
  if (!document.getElementById("unitscout-styles")) {
    const style = document.createElement("style");
    style.id = "unitscout-styles";
    style.textContent = `
      html, body { overflow-x: hidden; }
      input::placeholder { color: #6B7280; opacity: 1; }
      .history-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 12px;
      }
      @media (min-width: 768px) {
        .history-grid {
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
      }
      .history-card {
        background: #1C1F2E;
        border: 1px solid rgba(255,255,255,0.07);
        border-radius: 12px;
        overflow: hidden;
        transition: border-color 0.15s;
      }
      .history-card:hover {
        border-color: rgba(255,255,255,0.14);
      }
      .nav-logo { font-size: 15px; }
      .nav-tab { padding: 0 10px; font-size: 12px; }
      @media (max-width: 380px) {
        .nav-logo { font-size: 13px; }
        .nav-tab { padding: 0 7px; font-size: 11px; }
      }
    `;
    document.head.appendChild(style);
  }
};

const redirectToCheckout = async (plan, userId, email, stripeCustomerId = null) => {
  try {
    const priceId = STRIPE_PRICES[plan] || STRIPE_PRICES.pro;
    const response = await fetch("/api/create-checkout-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ plan, priceId, userId, email, stripeCustomerId }),
    });
    const data = await response.json();
    if (data.url) { window.location.href = data.url; }
    else { throw new Error(data.error || "Failed to create checkout session"); }
  } catch (err) {
    console.error("Checkout error:", err);
    alert("Payment setup failed. Please try again.");
  }
};

const redirectToPortal = async (stripeCustomerId) => {
  try {
    const response = await fetch("/api/create-portal-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ stripeCustomerId }),
    });
    const data = await response.json();
    if (data.url) { window.location.href = data.url; }
    else { throw new Error(data.error || "Failed to open customer portal"); }
  } catch (err) {
    console.error("Portal error:", err);
    alert("Could not open subscription portal. Please contact support@myunitscout.com");
  }
};

const uploadPhotoToStorage = async (base64Data, mediaType, userId, scanId, index) => {
  try {
    const byteString = atob(base64Data);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
    const blob = new Blob([ab], { type: mediaType });
    const ext = mediaType.includes("png") ? "png" : "jpg";
    const path = `${userId}/${scanId}/photo_${index}.${ext}`;
    const { error } = await supabase.storage.from("scan-photos").upload(path, blob, { contentType: mediaType, upsert: true });
    if (error) { console.error("Upload error:", error); return null; }
    const { data: signedData, error: signedError } = await supabase.storage.from("scan-photos").createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
    if (signedError) return null;
    return signedData.signedUrl;
  } catch { return null; }
};

const getUserLocation = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
          const d = await res.json();
          resolve({
            lat, lng,
            city: d.address?.city || d.address?.town || d.address?.village || null,
            state: d.address?.state || null,
          });
        } catch { resolve({ lat, lng, city: null, state: null }); }
      },
      () => resolve(null),
      { timeout: 5000 }
    );
  });

const findNearbyStorageFacility = async (lat, lng) => {
  try {
    const { data, error } = await supabase.rpc('match_facility', {
      user_lat: lat,
      user_lng: lng,
      radius_meters: 1200
    });
    if (error) throw error;
    if (!data || data.length === 0) return { found: false };
    return {
      found: true,
      name: data[0].name,
      address: data[0].address,
      city: data[0].city,
      state: data[0].state,
      id: data[0].id,
      multiple: data.length > 1,
      options: data
    };
  } catch (err) {
    console.error('Facility match error:', err);
    return { found: false };
  }
};

// -- Inline Outcome Section ---------------------------------------------------
function OutcomeSection({ scanId, userId, existingOutcome, existingCategory, onOutcomeSet, isOnlineBidder }) {
  const [selected, setSelected] = useState(existingOutcome?.outcome_type || null);
  const [winningBid, setWinningBid] = useState(existingOutcome?.winning_bid?.toString() || "");
  const [finalBid, setFinalBid] = useState(existingOutcome?.final_bid_lost?.toString() || "");
  const [reminderWeeks, setReminderWeeks] = useState(2);
  const [category, setCategory] = useState(existingCategory || null);
  const [mixedPicks, setMixedPicks] = useState([]);
  const [hasBoxes, setHasBoxes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(!!existingOutcome);

  const getDateFromNow = (weeks) => {
    const d = new Date();
    d.setDate(d.getDate() + weeks * 7);
    return d.toISOString().split("T")[0];
  };

  const handleSelect = async (type) => {
    if (saving || !scanId) return;
    if (saved && selected === type) return;
    setSelected(type);
    setSaved(false);
    setCategory(null);
    setMixedPicks([]);
    setHasBoxes(null);
    if (type === "passed" || type === "still_bidding") {
      setSaving(true);
      await supabase.from("logs").update({ outcome_type: type, winning_bid: null }).eq("id", scanId).eq("user_id", userId);
      setSaved(true);
      if (onOutcomeSet) onOutcomeSet(type, null);
      setSaving(false);
    }
  };

  const canSaveLost = finalBid.trim() !== "" && parseFloat(finalBid) > 0;

  const handleSaveLost = async () => {
    if (!canSaveLost || saved || saving) return;
    setSaving(true);
    await supabase.from("logs").update({ outcome_type: "lost", winning_bid: parseFloat(finalBid) }).eq("id", scanId).eq("user_id", userId);
    setSaved(true);
    if (onOutcomeSet) onOutcomeSet("lost", null);
    setSaving(false);
  };

  const toggleMixedPick = (cat) => {
    setMixedPicks((prev) => {
      if (prev.includes(cat)) return prev.filter((c) => c !== cat);
      if (prev.length >= 2) return prev;
      return [...prev, cat];
    });
  };

  const finalCategory = null; // category removed from outcome flow
  const bidEntered = winningBid.trim() !== "" && parseFloat(winningBid) > 0;
  const canSaveWon = bidEntered;

  const handleSaveWon = async () => {
    if (!canSaveWon || saved || saving) return;
    setSaving(true);
    await supabase.from("logs").update({ outcome_type: "won", winning_bid: parseFloat(winningBid) }).eq("id", scanId).eq("user_id", userId);
    setSaved(true);
    if (onOutcomeSet) onOutcomeSet("won", null);
    setSaving(false);
  };

  const borderColor = C.border;
  const bgColor = C.bgCard;

  return (
    <div style={{
      background: bgColor, border: `2px solid ${borderColor}`, borderRadius: 12,
      padding: 20, marginBottom: 20, transition: "all 0.2s",
    }}>
      {/* Two outcome buttons — Won and Passed only */}
      <div style={{ display: "grid", gridTemplateColumns: isOnlineBidder ? "1fr 1fr 1fr" : "1fr 1fr", gap: 8 }}>
        {[
          { type: "won", label: "WON", color: C.green },
          { type: "passed", label: "PASSED", color: "#9CA3AF" },
          ...(isOnlineBidder ? [{ type: "still_bidding", label: "WATCHING", color: C.yellow }] : []),
        ].map(({ type, label, color }) => {
          const isSelected = selected === type;
          return (
            <button key={type} onClick={() => handleSelect(type)}
              style={{
                padding: "18px 4px", borderRadius: 8,
                border: `2px solid ${color}`,
                background: isSelected ? color : `${color}20`,
                color: isSelected ? (type === "still_bidding" || type === "passed" ? "#000" : C.white) : color,
                fontSize: 13, fontWeight: 700, cursor: "pointer",
                transition: "all 0.15s", lineHeight: 1.2, textAlign: "center",
                opacity: isSelected ? 1 : 0.75,
              }}>
              {label}
            </button>
          );
        })}
      </div>

      {/* Won — just bid amount */}
      {selected === "won" && !saved && (
        <div style={{ marginTop: 20 }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: C.grey, fontSize: 13, marginBottom: 8 }}>Winning bid</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: C.grey, fontSize: 16 }}>$</span>
              <input type="number" inputMode="decimal" placeholder="0.00" autoFocus
                value={winningBid} onChange={(e) => setWinningBid(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && canSaveWon) handleSaveWon(); }}
                style={{ ...S.input, flex: 1, border: `2px solid ${bidEntered ? C.green : C.green}50` }} />
            </div>
          </div>
          <button onClick={handleSaveWon} disabled={!canSaveWon || saving}
            style={{ width: "100%", padding: "13px", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: canSaveWon && !saving ? "pointer" : "not-allowed", border: "none", background: canSaveWon ? C.green : `${C.green}30`, color: canSaveWon ? C.white : `${C.green}70`, transition: "all 0.15s" }}>
            {saving ? "Saving..." : "Save Won Unit"}
          </button>
        </div>
      )}

      {/* Saved confirmation */}
      {saved && selected && (
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 15, color: OUTCOME_COLORS[selected], fontWeight: 700 }}>
          {selected === "won" ? "Log your recovery amount to complete your ROI." :
           selected === "passed" ? "Saved" :
           "Saved — update when you know!"}
        </div>
      )}

      {!scanId && (
        <div style={{ color: C.grey, fontSize: 11, textAlign: "center", marginTop: 10, opacity: 0.6 }}>
          Saving scan first...
        </div>
      )}
    </div>
  );
}


// -- Items List ----------------------------------------------------------------
// -- Market Lookup -------------------------------------------------------------
function MarketLookup({ customUrls }) {
  // Deep link: tries to open app, falls back to web
  const openLink = (appUrl, webUrl) => {
    const start = Date.now();
    window.location.href = appUrl;
    setTimeout(() => {
      if (Date.now() - start < 2000) window.open(webUrl, "_blank");
    }, 1000);
  };

  const links = [
    {
      label: "eBay",
      color: "#E53238",
      onPress: () => openLink("ebay://search?query=storage+unit", "https://www.ebay.com/sch/i.html?LH_Sold=1&LH_Complete=1&_sop=13"),
    },
    {
      label: "Facebook",
      color: "#1877F2",
      onPress: () => openLink("fb://marketplace", "https://www.facebook.com/marketplace/"),
    },
    {
      label: "OfferUp",
      color: "#19A463",
      onPress: () => openLink("offerup://", "https://offerup.com/"),
    },
    ...(customUrls || []).filter(u => u.label && u.url).map(u => ({
      label: u.label,
      color: C.gold,
      onPress: () => window.open(u.url.replace("{query}", ""), "_blank"),
    })),
  ];

  return (
    <div style={{ ...S.card, marginBottom: 16, border: `2px solid ${C.border}` }}>
      <div style={{ color: C.grey, fontSize: 11, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 14 }}>
        Market Lookup
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {links.map((link) => (
          <button key={link.label} onClick={link.onPress}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: 14, fontWeight: 600, textDecoration: "none", cursor: "pointer",
              background: `${link.color}15`, border: `1px solid ${link.color}50`,
              color: link.color, borderRadius: 8, padding: "10px 18px",
            }}>
            {link.label} ↗
          </button>
        ))}
      </div>
    </div>
  );
}

// -- Photo Carousel ------------------------------------------------------------
function PhotoCarousel({ photos, localPhotos, onSetPrimary }) {
  const allPhotos = photos?.length ? photos : localPhotos || [];
  const [current, setCurrent] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  if (!allPhotos.length) return null;

  const prev = (e) => { e?.stopPropagation(); setCurrent((c) => (c - 1 + allPhotos.length) % allPhotos.length); };
  const next = (e) => { e?.stopPropagation(); setCurrent((c) => (c + 1) % allPhotos.length); };

  return (
    <>
      {/* Fullscreen */}
      {fullscreen && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#000000EE", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setFullscreen(false)}>
          <button onClick={() => setFullscreen(false)} style={{ position: "absolute", top: 20, right: 20, background: "none", border: "none", color: C.white, fontSize: 32, cursor: "pointer" }}>✕</button>
          {allPhotos.length > 1 && <button onClick={prev} style={{ position: "absolute", left: 16, background: "none", border: "none", color: C.white, fontSize: 48, cursor: "pointer" }}>‹</button>}
          <img src={allPhotos[current]} alt="Full view" style={{ maxWidth: "95vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8 }} onClick={(e) => e.stopPropagation()} />
          {allPhotos.length > 1 && <button onClick={next} style={{ position: "absolute", right: 16, background: "none", border: "none", color: C.white, fontSize: 48, cursor: "pointer" }}>›</button>}
          <div style={{ position: "absolute", bottom: 20, color: C.grey, fontSize: 13 }}>{current + 1} / {allPhotos.length}</div>
        </div>
      )}

      {/* Main carousel */}
      <div style={{ position: "relative", marginBottom: 16, borderRadius: 12, overflow: "hidden", borderTop: `0.5px solid ${C.border}40` }}>
        {/* Photo */}
        <img
          src={allPhotos[current]}
          alt={`Photo ${current + 1}`}
          onClick={() => setFullscreen(true)}
          style={{ width: "100%", maxHeight: "46vh", objectFit: "cover", display: "block", cursor: "zoom-in" }}
        />

        {/* Left arrow */}
        {allPhotos.length > 1 && (
          <button onClick={prev} style={{
            position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
            background: "#00000080", border: "none", color: C.white, fontSize: 28,
            width: 40, height: 40, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          }}>‹</button>
        )}

        {/* Right arrow */}
        {allPhotos.length > 1 && (
          <button onClick={next} style={{
            position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
            background: "#00000080", border: "none", color: C.white, fontSize: 28,
            width: 40, height: 40, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          }}>›</button>
        )}

        {/* Counter + Set Primary */}
        {allPhotos.length > 1 && (
          <div style={{
            position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)",
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <div style={{ background: "#00000090", borderRadius: 20, padding: "4px 12px", color: C.white, fontSize: 12 }}>
              {current + 1} / {allPhotos.length}
            </div>
            {onSetPrimary && current !== 0 && (
              <button onClick={() => { onSetPrimary(current); setCurrent(0); }}
                style={{
                  background: "#00000090", border: `1px solid ${C.gold}`, borderRadius: 20,
                  color: C.gold, fontSize: 11, padding: "4px 12px", cursor: "pointer", fontWeight: 600,
                }}>
                Set as primary
              </button>
            )}
            {current === 0 && null}
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {allPhotos.length > 1 && (
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, marginBottom: 8 }}>
          {allPhotos.map((photo, i) => (
            <div key={i} onClick={() => setCurrent(i)}
              style={{ flexShrink: 0, borderRadius: 6, overflow: "hidden", cursor: "pointer", border: `2px solid ${i === current ? C.gold : "transparent"}`, transition: "border-color 0.15s" }}>
              <img src={photo} alt={`Thumb ${i + 1}`} style={{ width: 52, height: 40, objectFit: "cover", display: "block" }} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// -- Phase 2 - Box Contents ---------------------------------------------------
// -- Phase 3 - Resale Results -------------------------------------------------
function Phase3Section({ scanId, userId, winningBid, onComplete }) {
  const [totalSold, setTotalSold] = useState("");
  const [liquidationTime, setLiquidationTime] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const liquidationOptions = [
    { value: "Under 1 week", label: "Under 1 wk" },
    { value: "1–2 weeks", label: "1–2 wks" },
    { value: "2–4 weeks", label: "2–4 wks" },
    { value: "1–3 months", label: "1–3 mo" },
  ];

  const canSave = totalSold.trim() !== "" && parseFloat(totalSold) >= 0 && liquidationTime !== "";

  const handleSave = async () => {
    if (!canSave || done || saving) return;
    setSaving(true);
    const sold = parseFloat(totalSold);
    const roi = winningBid && winningBid > 0
      ? parseFloat((((sold - winningBid) / winningBid) * 100).toFixed(1))
      : null;
    await supabase.from("logs").update({
      phase_complete: true,
      total_recovered: sold,
      liquidation_time: liquidationTime,
      roi: roi,
    }).eq("id", scanId).eq("user_id", userId);
    setDone(true);
    setSaving(false);
    if (onComplete) onComplete({ total_sold: sold, roi });
  };

  if (done) {
    const sold = parseFloat(totalSold);
    const roi = winningBid && winningBid > 0
      ? (((sold - winningBid) / winningBid) * 100).toFixed(0)
      : null;
    return (
      <div style={{ background: `${C.green}15`, border: `1px solid ${C.green}50`, borderRadius: 12, padding: 20, textAlign: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🔥</div>
        <div style={{ color: C.green, fontSize: 22, fontWeight: 800, marginBottom: 4 }}>
          ${sold.toLocaleString()} recovered
        </div>
        {roi !== null && (
          <div style={{ color: C.white, fontSize: 16, fontWeight: 600 }}>{roi}% ROI</div>
        )}
        <div style={{ color: C.grey, fontSize: 13, marginTop: 8 }}>Results logged ✓</div>
      </div>
    );
  }

  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>

      {/* Live profit dashboard — shows as they type */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          {winningBid && (
            <div>
              <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Winning Bid</div>
              <div style={{ color: C.white, fontWeight: 700, fontSize: 18 }}>${winningBid.toLocaleString()}</div>
            </div>
          )}
          <div>
            <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Total Recovered</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: C.grey, fontSize: 14 }}>$</span>
              <input type="number" inputMode="decimal" placeholder="0.00" autoFocus
                value={totalSold} onChange={(e) => setTotalSold(e.target.value)}
                style={{ ...S.input, flex: 1, fontSize: 18, fontWeight: 700, padding: "4px 8px" }} />
            </div>
          </div>
          {totalSold !== "" && winningBid && (
            <>
              <div>
                <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Profit</div>
                <div style={{ color: (parseFloat(totalSold) - winningBid) >= 0 ? C.green : C.red, fontWeight: 700, fontSize: 18 }}>
                  ${(parseFloat(totalSold) - winningBid).toLocaleString()} {(parseFloat(totalSold) - winningBid) >= 0 ? "✓" : ""}
                </div>
              </div>
              <div>
                <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>ROI</div>
                <div style={{ color: (parseFloat(totalSold) - winningBid) >= 0 ? C.green : C.red, fontWeight: 800, fontSize: 22 }}>
                  {(((parseFloat(totalSold) - winningBid) / winningBid) * 100).toFixed(0)}%
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Unit Turnover Time */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ color: C.grey, fontSize: 13, marginBottom: 8 }}>Unit Liquidation Time</div>
        <div style={{ display: "flex", gap: 6 }}>
          {liquidationOptions.map(({ value, label }) => (
            <button key={value} onClick={() => setLiquidationTime(value)}
              style={{
                flex: 1, padding: "8px 4px", borderRadius: 8, fontSize: 11, textAlign: "center",
                border: `1px solid ${liquidationTime === value ? C.green : C.border}`,
                background: liquidationTime === value ? `${C.green}20` : C.bgDeep,
                color: liquidationTime === value ? C.green : C.grey,
                cursor: "pointer", fontWeight: liquidationTime === value ? 600 : 400,
              }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Where sold — multi-select */}
      {saving && <div style={{ color: C.grey, fontSize: 12, textAlign: "center", marginTop: 8 }}>Saving...</div>}

      {/* Explicit save — green */}
      {!saving && (
        <button onClick={handleSave} disabled={!canSave}
          style={{ width: "100%", padding: "13px", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: canSave ? "pointer" : "not-allowed", border: "none", background: canSave ? C.green : `${C.green}25`, color: canSave ? C.white : `${C.green}60`, marginTop: 8, transition: "all 0.15s" }}>
          Save Closing Data
        </button>
      )}
    </div>
  );
}

// -- Scanner Page --------------------------------------------------------------
function ScannerPage({ user, onScanComplete, onGoToSettings }) {
  const isTrialUser = user?.plan === "free";
  const trialEnd = isTrialUser && user?.currentPeriodEnd ? new Date(user.currentPeriodEnd) : null;
  const trialExpired = trialEnd ? new Date() > trialEnd : false;

  if (trialExpired) {
    return (
      <div style={{ ...S.page, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", padding: "40px 24px", textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 20 }}>🔒</div>
        <h2 style={{ color: C.white, fontSize: 22, fontWeight: 700, marginBottom: 12 }}>Your trial has ended</h2>
        <p style={{ color: C.grey, fontSize: 15, lineHeight: 1.7, maxWidth: 360, marginBottom: 32 }}>
          Upgrade to UnitScout+ to keep logging units. Your existing data is safe and waiting in History.
        </p>
        <button onClick={() => redirectToCheckout("pro", user.id, user.email)} style={{ ...S.goldBtn, fontSize: 15, padding: "13px 32px" }}>
          Get UnitScout+ — $5.99/mo
        </button>
      </div>
    );
  }

  const [images, setImages] = useState([]);
  const [imageBase64s, setImageBase64s] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savedScanId, setSavedScanId] = useState(null);
  const [unitLabel, setUnitLabel] = useState("");
  const [editingLabel, setEditingLabel] = useState(false);
  const [outcomeSet, setOutcomeSet] = useState(null);
  const [location, setLocation] = useState(null);
  const [detectedFacility, setDetectedFacility] = useState(null);
  const [facilityLoading, setFacilityLoading] = useState(false);
  const [auctionTypeOverride, setAuctionTypeOverride] = useState(null);
  const [scanDate, setScanDate] = useState(null);
  const [customUrls, setCustomUrls] = useState([]);
  // Facility search sheet
  const [showFacilitySheet, setShowFacilitySheet] = useState(false);
  const [facilityQuery, setFacilityQuery] = useState("");
  const [facilityResults, setFacilityResults] = useState([]);
  const [facilitySearching, setFacilitySearching] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [showAddFacility, setShowAddFacility] = useState(false);
  const [newFacilityName, setNewFacilityName] = useState("");
  const [newFacilityAddress, setNewFacilityAddress] = useState("");
  const [addingFacility, setAddingFacility] = useState(false);
  const fileRef = useRef();
  const cameraRef = useRef();

  // Load persisted facility from localStorage (60 min session)
  useEffect(() => {
    try {
      const stored = localStorage.getItem("us_facility");
      if (stored) {
        const { facility, ts } = JSON.parse(stored);
        if (Date.now() - ts < 60 * 60 * 1000) {
          setSelectedFacility(facility);
        } else {
          localStorage.removeItem("us_facility");
        }
      }
    } catch {}
  }, []);

  const saveFacilityToStorage = (facility) => {
    try {
      localStorage.setItem("us_facility", JSON.stringify({ facility, ts: Date.now() }));
    } catch {}
  };

  const clearFacilityStorage = () => {
    try { localStorage.removeItem("us_facility"); } catch {}
  };

  const isFacilitySessionValid = () => {
    try {
      const stored = localStorage.getItem("us_facility");
      if (!stored) return false;
      const { ts } = JSON.parse(stored);
      return Date.now() - ts < 60 * 60 * 1000;
    } catch { return false; }
  };

  useEffect(() => {
    const loadCustomUrls = async () => {
      if (!user?.id) return;
      const { data } = await supabase.from("profiles").select("custom_market_urls").eq("id", user.id).single();
      if (data?.custom_market_urls) setCustomUrls(data.custom_market_urls);
    };
    loadCustomUrls();
  }, []);

  // Google Places address autocomplete using JS SDK
  const searchFacilities = (query) => {
    if (!query.trim() || query.trim().length < 1) { setFacilityResults([]); return; }
    if (!window.google?.maps?.places) { setFacilityResults([]); return; }
    setFacilitySearching(true);
    const svc = new window.google.maps.places.AutocompleteService();
    const isAddressInput = /^\d/.test(query.trim());
    const searchQuery = isAddressInput || query.toLowerCase().includes("storage")
      ? query
      : `${query} storage`;
    svc.getPlacePredictions(
      {
        input: searchQuery,
        types: ["establishment"],
        componentRestrictions: { country: "us" },
        keyword: "storage facility",
      },
      (predictions, status) => {
        setFacilitySearching(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setFacilityResults(predictions);
        } else {
          setFacilityResults([]);
        }
      }
    );
  };

  // Resolve place_id to get name + formatted_address
  const selectPlacePrediction = (prediction) => {
    if (!window.google?.maps?.places) return;
    const div = document.createElement("div");
    const svc = new window.google.maps.places.PlacesService(div);
    svc.getDetails(
      { placeId: prediction.place_id, fields: ["name", "formatted_address"] },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          selectFacility({
            name: place.name,
            address: place.formatted_address,
            id: null,
          });
        } else {
          selectFacility({
            name: prediction.structured_formatting?.main_text || prediction.description,
            address: prediction.description,
            id: null,
          });
        }
      }
    );
  };

  // Open Log Unit — show facility sheet first (unless online bidder or session active)
  const openLogUnit = async () => {
    if (user?.isOnlineBidder) {
      fileRef.current.click();
      return;
    }
    setFacilityQuery("");
    setShowAddFacility(false);
    setNewFacilityName("");
    setNewFacilityAddress("");
    setFacilityResults([]);
    setShowFacilitySheet(true);
  };

  useEffect(() => {
    const handler = () => { openLogUnit(); };
    window.addEventListener("unitscout-open-camera", handler);
    return () => window.removeEventListener("unitscout-open-camera", handler);
  }, [user?.isOnlineBidder, selectedFacility]);

  const selectFacility = (facility) => {
    setSelectedFacility(facility);
    saveFacilityToStorage(facility);
    setShowFacilitySheet(false);
    fileRef.current.click();
  };

  const addNewFacility = async () => {
    if (!newFacilityName.trim()) return;
    setAddingFacility(true);
    try {
      const loc = location || await getUserLocation();
      const { data, error } = await supabase.from("facilities").insert({
        name: newFacilityName.trim(),
        address: newFacilityAddress.trim() || null,
        city: loc?.city || null,
        state: loc?.state || null,
        latitude: loc?.lat || null,
        longitude: loc?.lng || null,
      }).select().single();
      if (data) {
        selectFacility({ id: data.id, name: data.name, address: data.address, city: data.city, state: data.state });
      }
    } catch {}
    setAddingFacility(false);
  };

  const handleImages = (files) => {
    const newImages = [], newBase64s = [];
    let processed = 0;
    const toProcess = Math.min(files.length, 6 - images.length);
    if (toProcess === 0) return;
    Array.from(files).slice(0, toProcess).forEach((file) => {
      const mediaType = file.type || "image/jpeg";
      const reader = new FileReader();
      reader.onload = (e) => {
        newImages.push(e.target.result);
        newBase64s.push({ data: e.target.result.split(",")[1], mediaType, file });
        processed++;
        if (processed === toProcess) {
          setImages((prev) => [...prev, ...newImages]);
          setImageBase64s((prev) => [...prev, ...newBase64s]);
          setResult(null); setError(null);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImageBase64s((prev) => prev.filter((_, i) => i !== index));
    setResult(null);
  };

  const uploadPhotosInBackground = async (scanId, base64s, userId) => {
    try {
      const urls = await Promise.all(
        base64s.map((b64, i) => uploadPhotoToStorage(b64.data, b64.mediaType, userId, scanId, i))
      );
      const validUrls = urls.filter(Boolean);
      if (validUrls.length > 0) {
        await supabase.from("logs").update({ photo_urls: validUrls }).eq("id", scanId);
      }
    } catch (err) { console.error("Background photo upload failed:", err); }
  };

  const logUnit = async () => {
    if (!imageBase64s.length) return;
    setLoading(true); setError(null);
    try {
      const now = new Date();
      setScanDate(now);

      // Get and increment unit counter from profile
      const { data: profileData } = await supabase.from("profiles").select("unit_counter").eq("id", user.id).single();
      const nextCounter = (profileData?.unit_counter || 0) + 1;
      await supabase.from("profiles").update({ unit_counter: nextCounter }).eq("id", user.id);
      const autoLabel = `Unit ${nextCounter}`;

      // Resolve facility for this scan
      const facilityForScan = user?.isOnlineBidder
        ? { name: "Online", address: null, id: null }
        : selectedFacility || null;

      const { data: insertedScan } = await supabase.from("logs").insert({
        user_id: user.id,
        unit_label: autoLabel,
        date: now.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
        time: now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
        photo_urls: [],
        facility_name: facilityForScan?.name || null,
        facility_address: facilityForScan?.address || null,
        facility_latitude: null,
        facility_longitude: null,
        facility_place_id: null,
        outcome_type: null,
        bid_amount: null,
        winning_bid: null,
        total_recovered: null,
        liquidation_time: null,
        roi: null,
        phase_complete: false,
        platform: "web",
      }).select().single();

      if (insertedScan) {
        setSavedScanId(insertedScan.id);
        setUnitLabel(autoLabel);
        setResult({ items: [] });
        if (facilityForScan) setDetectedFacility(facilityForScan);
        if (selectedFacility) saveFacilityToStorage(selectedFacility);
        uploadPhotosInBackground(insertedScan.id, imageBase64s, user.id);
      }
      if (onScanComplete) onScanComplete();
    } catch (err) { setError("Failed to save: " + err.message); }
    setLoading(false);
  };

  const handleLabelSave = async () => {
    setEditingLabel(false);
    if (savedScanId && unitLabel.trim()) {
      await supabase.from("logs").update({ unit_label: unitLabel.trim() }).eq("id", savedScanId);
    }
  };

  const reset = () => {
    setImages([]); setImageBase64s([]); setResult(null); setError(null);
    setSavedScanId(null); setUnitLabel(""); setEditingLabel(false);
    setOutcomeSet(null); setLocation(null); setDetectedFacility(null);
    setFacilityLoading(false); setAuctionTypeOverride(null); setScanDate(null);
  };

  return (
    <div>
      {/* ── Upload Zone ── */}
      {!result && (
        <div>
          {images.length === 0 ? (
            /* Empty state — full page tap target */
            <div onClick={openLogUnit}
              style={{
                minHeight: "60vh", display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", cursor: "pointer",
                gap: 24,
              }}>
              <Camera size={72} color={C.gold} strokeWidth={1.2} />
              <button onClick={(e) => { e.stopPropagation(); openLogUnit(); }}
                style={{ ...S.goldBtn, fontSize: 16, padding: "14px 40px", borderRadius: 10 }}>
                Log Unit
              </button>
              <div style={{ color: C.grey, fontSize: 12, textAlign: "center", lineHeight: 1.6, whiteSpace: "nowrap" }}>
                Photograph the unit. Log the outcome. Your record builds itself.
              </div>
            </div>
          ) : (
            /* Photos added state */
            <div>
              {/* Primary photo — capped at 58vh so everything below stays visible */}
              <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", marginBottom: 12 }}>
                <img src={images[0]} alt="Unit" style={{ width: "100%", maxHeight: "58vh", objectFit: "cover", display: "block" }} />
                <button onClick={() => removeImage(0)}
                  style={{ position: "absolute", top: 10, right: 10, background: "#00000090", border: "none", borderRadius: "50%", width: 30, height: 30, color: C.white, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  ✕
                </button>
              </div>

              {/* Additional photos strip */}
              {images.length > 1 && (
                <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, marginBottom: 12 }}>
                  {images.slice(1).map((img, i) => (
                    <div key={i + 1} style={{ position: "relative", flexShrink: 0, borderRadius: 8, overflow: "hidden" }}>
                      <img src={img} alt={`Photo ${i + 2}`} style={{ width: 170, height: 130, objectFit: "cover", display: "block" }} />
                      <button onClick={() => removeImage(i + 1)}
                        style={{ position: "absolute", top: 5, right: 5, background: "#00000090", border: "none", borderRadius: "50%", width: 24, height: 24, color: C.white, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add more — hidden silently at 20 */}
              {images.length < 6 && (
                <button onClick={() => fileRef.current.click()}
                  style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "7px 16px", color: C.grey, fontSize: 13, cursor: "pointer", marginBottom: 16 }}>
                  + Add more photos
                </button>
              )}

              {/* Log Unit CTA */}
              <button onClick={logUnit} disabled={loading}
                style={{ ...S.goldBtn, width: "100%", fontSize: 16, padding: 16, borderRadius: 10, opacity: loading ? 0.6 : 1, cursor: loading ? "not-allowed" : "pointer" }}>
                {loading ? "Logging unit..." : "Log Unit →"}
              </button>
              <div style={{ color: C.grey, fontSize: 12, textAlign: "center", marginTop: 10, lineHeight: 1.5, opacity: 0.7, whiteSpace: "nowrap" }}>
                Photograph the unit. Log the outcome. Your record builds itself.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ FACILITY SEARCH SHEET ═══ */}
      {showFacilitySheet && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
          onClick={() => setShowFacilitySheet(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.bgCard, borderRadius: 16, width: "100%", maxWidth: 540, maxHeight: "75vh", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>

            {/* Header */}
            <div style={{ padding: "20px 20px 12px", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Which facility are you at?</div>
              <div style={{ color: C.grey, fontSize: 12, marginBottom: 12 }}>Type the facility name or address</div>
              <input
                autoFocus
                type="text"
                placeholder="e.g. Public Storage, Extra Space..."
                value={facilityQuery}
                onChange={e => {
                  setFacilityQuery(e.target.value);
                  clearTimeout(window._facilitySearchTimer);
                  window._facilitySearchTimer = setTimeout(() => searchFacilities(e.target.value), 150);
                }}
                style={{ ...S.input, fontSize: 14 }}
              />
            </div>

            {/* Results */}
            <div style={{ flex: 1, overflowY: "auto", padding: "4px 0" }}>
              {facilitySearching && (
                <div style={{ color: C.grey, fontSize: 13, textAlign: "center", padding: 20 }}>Searching...</div>
              )}

              {!facilitySearching && facilityQuery.trim().length < 2 && (
                <div style={{ color: C.grey, fontSize: 13, textAlign: "center", padding: 24 }}>Start typing to search for a facility</div>
              )}

              {/* Google Places predictions */}
              {!facilitySearching && facilityResults.map((pred) => {
                const name = pred.structured_formatting?.main_text || pred.description;
                const fullAddress = pred.description.startsWith(name)
                  ? pred.description.slice(name.length).replace(/^,\s*/, "")
                  : pred.structured_formatting?.secondary_text || pred.description;
                return (
                  <div key={pred.place_id} onClick={() => selectPlacePrediction(pred)}
                    style={{ padding: "13px 20px", cursor: "pointer", borderBottom: `1px solid ${C.border}20` }}
                    onMouseEnter={e => e.currentTarget.style.background = C.bgDeep}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <div style={{ color: C.white, fontSize: 14, fontWeight: 700, marginBottom: 3 }}>{name}</div>
                    <div style={{ color: C.grey, fontSize: 12 }}>{fullAddress}</div>
                  </div>
                );
              })}

              {!facilitySearching && facilityResults.length === 0 && facilityQuery.trim().length >= 2 && (
                <div style={{ color: C.grey, fontSize: 13, textAlign: "center", padding: 20 }}>No results — try a different address or name</div>
              )}
            </div>

            {/* Skip option */}
            <div style={{ padding: "12px 20px", borderTop: `1px solid ${C.border}`, textAlign: "center" }}>
              <button onClick={() => { setShowFacilitySheet(false); setSelectedFacility(null); clearFacilityStorage(); fileRef.current.click(); }}
                style={{ background: "none", border: "none", color: C.grey, fontSize: 13, cursor: "pointer" }}>
                Skip — log without facility
              </button>
            </div>
          </div>
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => handleImages(e.target.files)} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(e) => handleImages(e.target.files)} />

      {error && (
        <div style={{ background: "#7f1d1d40", border: `1px solid ${C.red}`, borderRadius: 12, padding: 16, marginBottom: 16, fontSize: 14, color: "#fca5a5" }}>
          {error}
        </div>
      )}

      {/* ═══ SCAN RESULT ═══ */}
      {result && (
        <div style={{ paddingTop: 16 }}>
          {/* 1. Unit Info Card */}
          <div style={{ ...S.card, marginBottom: 16, border: `1px solid ${C.border}` }}>

            {/* Unit name — clickable to edit */}
            {editingLabel ? (
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <input autoFocus type="text" placeholder='e.g. "North Face Unit" or "Unit #48"'
                  value={unitLabel} onChange={(e) => setUnitLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleLabelSave(); if (e.key === "Escape") setEditingLabel(false); }}
                  style={{ ...S.input, flex: 1 }} />
                <button onClick={handleLabelSave} style={{ ...S.goldBtn, padding: "10px 20px", fontSize: 14 }}>Save</button>
              </div>
            ) : (
              <div onClick={() => setEditingLabel(true)} style={{ cursor: "text", marginBottom: 10 }}>
                <span style={{ color: unitLabel ? C.white : C.grey, fontSize: 18, fontWeight: unitLabel ? 700 : 400 }}>
                  {unitLabel || "Name this unit..."}
                </span>
              </div>
            )}

            {/* Date */}
            {scanDate && (
              <div style={{ color: C.grey, fontSize: 13, marginBottom: 6 }}>
                {scanDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </div>
            )}

            {/* Location + Facility */}
            {selectedFacility && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{selectedFacility.name}</div>
                {selectedFacility.address && (
                  <div style={{ color: C.grey, fontSize: 12, marginTop: 2 }}>{selectedFacility.address}</div>
                )}
              </div>
            )}

          </div>

          {/* 2. Photos */}
          <PhotoCarousel localPhotos={images} />

          {/* 3. Outcome Buttons */}
          <OutcomeSection
            scanId={savedScanId}
            userId={user.id}
            onOutcomeSet={setOutcomeSet}
            isOnlineBidder={user?.isOnlineBidder}
          />

          {/* 4. Log Another */}
          <button onClick={reset}
            style={{ ...S.goldBtn, width: "100%", fontSize: 16, padding: "16px", marginTop: 8, borderRadius: 12 }}>
            + Log Another Unit
          </button>
        </div>
      )}
    </div>
  );
}

// -- History Page --------------------------------------------------------------
function HistoryPage({ user, onPendingChange, onChangePage, defaultFilter, onFilterConsumed }) {
  const [scans, setScans] = useState([]);
  const [outcomes, setOutcomes] = useState({});
  const [selectedScan, setSelectedScan] = useState(null);
  const [showChangeOutcome, setShowChangeOutcome] = useState(false);
  const [showEditClosing, setShowEditClosing] = useState(false);
  const [phase3Input, setPhase3Input] = useState({ totalSold: "", liquidationTime: "", saving: false, dirty: false });
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [editingDetailName, setEditingDetailName] = useState(false);
  const [editingDetailNameValue, setEditingDetailNameValue] = useState("");
  const [filter, setFilter] = useState("all");
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editingLabelValue, setEditingLabelValue] = useState("");
  const [pendingActions, setPendingActions] = useState([]);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [sortBy, setSortBy] = useState("date_desc");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterDateRange, setFilterDateRange] = useState("all");
  const [filterAuctionType, setFilterAuctionType] = useState("all");
  const [filterFacility, setFilterFacility] = useState("all");
  const [cardImageIndex, setCardImageIndex] = useState({});
  const [dragStart, setDragStart] = useState({});
  const [facilitySearch, setFacilitySearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [shareMode, setShareMode] = useState(false);
  const [selectedForShare, setSelectedForShare] = useState(null);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [shareCardId, setShareCardId] = useState(null);
  const [shareIncludeFinancials, setShareIncludeFinancials] = useState(true);
  const PAGE_SIZE = 26;

  const setCardImg = (scanId, idx, total) => {
    setCardImageIndex(prev => ({ ...prev, [scanId]: Math.max(0, Math.min(idx, total - 1)) }));
  };

  const openScan = async (scan) => {
    setSelectedScan(scan);
    const { data } = await supabase.from("logs").select("*").eq("id", scan.id).single();
    if (data) {
      setSelectedScan(data);
      setScans(prev => prev.map(s => s.id === data.id ? data : s));
    }
  };

  useEffect(() => { loadData(); }, [user]);

  useEffect(() => {
    if (defaultFilter && defaultFilter !== "all") {
      setFilter(defaultFilter);
      setCurrentPage(1);
      if (onFilterConsumed) onFilterConsumed();
    }
  }, [defaultFilter]);

  const loadData = async () => {
    if (!user?.id) { setLoading(false); return; }
    const { data: scanData } = await supabase.from("logs").select("*").eq("user_id", user.id).order("created_at", { ascending: false });
    setScans(scanData || []);
    if (scanData?.length > 0) {
      const outcomeMap = {};
      (scanData || []).forEach((log) => {
        outcomeMap[log.id] = {
          scan_id: log.id,
          user_id: log.user_id,
          outcome_type: log.outcome_type,
          winning_bid: log.winning_bid,
          phase3_complete: log.phase_complete,
          phase3_total_sold: log.total_recovered,
          phase3_roi: log.roi,
          phase3_liquidation_time: log.liquidation_time,
        };
      });
      setOutcomes(outcomeMap);

      // Find pending phase actions
      const today = new Date().toISOString().split("T")[0];
      const pending = (scanData || []).filter((scan) => {
        const o = outcomeMap[scan.id];
        if (!o || o.outcome_type !== "won") return false;
        if (!o.phase3_complete) return true;
        return false;
      });
      setPendingActions(pending);
      if (onPendingChange) onPendingChange(pending.length > 0 || (scanData || []).some(s => !outcomeMap[s.id]));
    }
    setLoading(false);
  };

  const handleDelete = async (scanId) => {
    if (!window.confirm("Delete this scan? This cannot be undone.")) return;
    setDeletingId(scanId);
    await supabase.from("logs").delete().eq("id", scanId).eq("user_id", user.id);
    setScans((prev) => prev.filter((s) => s.id !== scanId));
    setDeletingId(null);
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("Delete all scan history? This cannot be undone.")) return;
    setDeletingId("all");
    await supabase.from("logs").delete().eq("user_id", user.id);
    setScans([]);
    setDeletingId(null);
  };

  const handleLabelSave = async (scanId) => {
    await supabase.from("logs").update({ unit_label: editingLabelValue.trim() || null }).eq("id", scanId);
    setScans((prev) => prev.map((s) => s.id === scanId ? { ...s, unit_label: editingLabelValue.trim() || null } : s));
    setEditingLabelId(null);
  };

  const getFilteredScans = () => {
    let filtered = scans.filter((scan) => {
      const o = outcomes[scan.id];
      if (sortBy === "bid_desc" || sortBy === "roi_desc" || sortBy === "liq_time_asc") return o?.outcome_type === "won" && o?.phase3_complete;
      if (filter === "all") return true;
      if (filter === "pending") {
        const isWonPending = o?.outcome_type === "won" && !o.phase3_complete;
        return !o || isWonPending;
      }
      if (filter === "won") return o?.outcome_type === "won" && !!o?.phase3_complete;
      return o?.outcome_type === filter;
    });

    // On pending tab, float won units (with outcome) above no-outcome units
    if (filter === "pending") {
      filtered = [...filtered].sort((a, b) => {
        const aWon = outcomes[a.id]?.outcome_type === "won" ? 0 : 1;
        const bWon = outcomes[b.id]?.outcome_type === "won" ? 0 : 1;
        return aWon - bWon;
      });
    }

    if (filterCategory !== "all") {
      filtered = filtered.filter((s) => s.primary_category === filterCategory);
    }

    if (filterDateRange !== "all") {
      const days = { "7d": 7, "30d": 30, "90d": 90, "6m": 180 }[filterDateRange];
      if (days) {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - days);
        filtered = filtered.filter((s) => new Date(s.created_at) >= cutoff);
      }
    }

    if (filterAuctionType !== "all") {
      filtered = filtered.filter((s) =>
        filterAuctionType === "online" ? s.online_auction : s.in_person
      );
    }

    if (filterFacility !== "all") {
      filtered = filtered.filter((s) => s.facility_name === filterFacility);
    }

    if (sortBy === "date_asc") {
      filtered = [...filtered].reverse();
    } else if (sortBy === "bid_desc") {
      filtered = [...filtered].sort((a, b) => {
        const bidA = outcomes[a.id]?.winning_bid ?? -1;
        const bidB = outcomes[b.id]?.winning_bid ?? -1;
        return bidB - bidA;
      });
    } else if (sortBy === "roi_desc") {
      filtered = [...filtered].sort((a, b) => {
        const roiA = outcomes[a.id]?.phase3_roi ?? null;
        const roiB = outcomes[b.id]?.phase3_roi ?? null;
        if (roiA === null && roiB === null) return 0;
        if (roiA === null) return 1;
        if (roiB === null) return -1;
        return roiB - roiA;
      });
    } else if (sortBy === "liq_time_asc") {
      const timeOrder = { "Under 1 week": 0, "1–2 weeks": 1, "2–4 weeks": 2, "1–3 months": 3, "Still selling": 4 };
      filtered = [...filtered].sort((a, b) => {
        const tA = timeOrder[outcomes[a.id]?.phase3_liquidation_time] ?? 99;
        const tB = timeOrder[outcomes[b.id]?.phase3_liquidation_time] ?? 99;
        return tA - tB;
      });
    } else if (sortBy === "location_az") {
      filtered = [...filtered].sort((a, b) => {
        const locA = (a.facility_name || a.location_city || "").toLowerCase();
        const locB = (b.facility_name || b.location_city || "").toLowerCase();
        return locA.localeCompare(locB);
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((s) =>
        (s.unit_label || "").toLowerCase().includes(q) ||
        (s.facility_name || "").toLowerCase().includes(q) ||
        (s.facility_address || "").toLowerCase().includes(q) ||
        (s.location_city || "").toLowerCase().includes(q) ||
        (s.location_state || "").toLowerCase().includes(q)
      );
    }

    return filtered;
  };

  const filteredScans = getFilteredScans();
  const totalPages = Math.ceil(filteredScans.length / PAGE_SIZE);
  const pagedScans = filteredScans.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Reset page when filter changes
  const setFilterAndPage = (fn) => { fn(); setCurrentPage(1); };

  // Derived: unique facilities with 2+ entries
  const facilityList = (() => {
    const counts = {};
    scans.forEach((s) => { if (s.facility_name) counts[s.facility_name] = (counts[s.facility_name] || 0) + 1; });
    return Object.keys(counts).filter((f) => counts[f] >= 1);
  })();

  // Phase3 auto-save - must be before any conditional returns
  const phase3CanSave = !!(selectedScan &&
    (outcomes[selectedScan.id]?.outcome_type === "won" && !outcomes[selectedScan.id]?.phase3_complete || (showEditClosing && phase3Input.dirty)) &&
    phase3Input.totalSold.trim() !== "" && parseFloat(phase3Input.totalSold) >= 0 &&
    phase3Input.liquidationTime !== "" && !phase3Input.saving);

  const handlePhase3Save = async () => {
    if (!phase3CanSave || !selectedScan) return;
    setPhase3Input(p => ({ ...p, saving: true }));
    const sold = parseFloat(phase3Input.totalSold);
    const bid = outcomes[selectedScan.id]?.winning_bid;
    const roi = bid && bid > 0 ? parseFloat((((sold - bid) / bid) * 100).toFixed(1)) : null;
    await supabase.from("logs").update({
      phase_complete: true,
      total_recovered: sold,
      liquidation_time: phase3Input.liquidationTime,
      roi: roi,
    }).eq("id", selectedScan.id).eq("user_id", user.id);
    setOutcomes(prev => ({ ...prev, [selectedScan.id]: { ...prev[selectedScan.id], phase3_complete: true, phase3_total_sold: sold, phase3_roi: roi, phase3_liquidation_time: phase3Input.liquidationTime } }));
    setPhase3Input(p => ({ ...p, saving: false, dirty: false }));
    setShowEditClosing(false);
  };

  if (loading) return <div style={{ textAlign: "center", color: C.grey, padding: 60 }}>Loading history...</div>;

  // -- Report/Detail View ---------------------------------------------------
  if (selectedScan) {
    const outcome = outcomes[selectedScan.id];
    const items = selectedScan.items || selectedScan.report?.items || [];
    const today = new Date().toISOString().split("T")[0];
    const phase3Pending = outcome?.outcome_type === "won" && !outcome?.phase3_complete;

    const handleSetPrimary = async (photoIndex) => {
      const photos = [...(selectedScan.photo_urls || [])];
      const [primary] = photos.splice(photoIndex, 1);
      photos.unshift(primary);
      await supabase.from("logs").update({ photo_urls: photos }).eq("id", selectedScan.id);
      setScans((prev) => prev.map((s) => s.id === selectedScan.id ? { ...s, photo_urls: photos } : s));
      setSelectedScan((prev) => ({ ...prev, photo_urls: photos }));
    };

    return (
      <div style={{ paddingTop: 16 }}>
        <button onClick={() => { setSelectedScan(null); setShowChangeOutcome(false); setShowEditClosing(false); setEditingDetailName(false); setPhase3Input({ totalSold: "", liquidationTime: "", saving: false, dirty: false }); }} style={{ ...S.outlineBtn, marginBottom: 28 }}>
          ← Back to History
        </button>

        {/* 1. Header — name + date/geo + outcome badge */}
        <div style={{ ...S.card, marginBottom: 16 }}>
          {/* Name row with share button */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 4 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {editingDetailName ? (
                <input
                  autoFocus
                  value={editingDetailNameValue}
                  onChange={(e) => setEditingDetailNameValue(e.target.value)}
                  onBlur={async () => {
                    const newName = editingDetailNameValue.trim();
                    if (newName && newName !== selectedScan.unit_label) {
                      await supabase.from("logs").update({ unit_label: newName }).eq("id", selectedScan.id);
                      setScans(prev => prev.map(s => s.id === selectedScan.id ? { ...s, unit_label: newName } : s));
                      setSelectedScan(prev => ({ ...prev, unit_label: newName }));
                    }
                    setEditingDetailName(false);
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setEditingDetailName(false); } }}
                  style={{ ...S.input, fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: C.white, padding: "2px 6px", width: "100%", marginBottom: 6 }}
                />
              ) : (
                <h2
                  onClick={() => { setEditingDetailNameValue(selectedScan.unit_label || ""); setEditingDetailName(true); }}
                  title="Tap to rename"
                  style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, margin: "0 0 6px", color: C.white, cursor: "text", display: "flex", alignItems: "center", gap: 8 }}>
                  {selectedScan.unit_label || <span style={{ color: C.grey, fontStyle: "italic" }}>Unnamed Unit</span>}
                  <span style={{ fontSize: 15, color: C.grey, opacity: 0.5, fontFamily: "sans-serif", flexShrink: 0 }}>✏️</span>
                </h2>
              )}
            </div>
            <button onClick={() => { setShareCardId(selectedScan.id); setShareIncludeFinancials(true); }}
              style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "4px 12px", color: C.grey, fontSize: 12, cursor: "pointer", flexShrink: 0, marginLeft: 12, marginTop: 4 }}>
              Share
            </button>
          </div>

          {/* Date / location row */}
          <div style={{ marginBottom: outcome ? 14 : 0 }}>
            <div style={{ color: C.grey, fontSize: 13 }}>{selectedScan.date} at {selectedScan.time}</div>
            {selectedScan.facility_name && (
              <div style={{ color: C.grey, fontSize: 13, marginTop: 4 }}>{selectedScan.facility_name}</div>
            )}
            {selectedScan.facility_address && (
              <div style={{ color: C.grey, fontSize: 12, marginTop: 2 }}>{selectedScan.facility_address}</div>
            )}
          </div>

          {/* Outcome badge — bottom of header card */}
          {outcome && (
            <span style={{
              display: "inline-block",
              background: `${OUTCOME_COLORS[outcome.outcome_type]}20`,
              color: OUTCOME_COLORS[outcome.outcome_type],
              border: `1px solid ${OUTCOME_COLORS[outcome.outcome_type]}40`,
              borderRadius: 20, padding: "4px 14px", fontSize: 13, fontWeight: 700,
            }}>
              {OUTCOME_LABELS[outcome.outcome_type]}
            </span>
          )}
        </div>

        {/* 2. Data card — always above photo */}
        {outcome?.outcome_type === "won" && (
          <div style={{ ...S.card, marginBottom: 16, border: `1px solid ${C.border}` }}>
            {(() => { const isEditing = phase3Pending || showEditClosing; return (<>

            {/* Section title + category */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid ${C.border}` }}>
              <div style={{ color: C.white, fontSize: 19, fontWeight: 700 }}>Unit Performance</div>
              {selectedScan.user_category && (
                <span style={{ color: C.grey, fontSize: 12 }}>
                  <span style={{ color: C.gold, fontWeight: 600 }}>{selectedScan.user_category}</span>
                </span>
              )}
            </div>

            {/* Winning Bid + Total Recovered */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              {outcome.winning_bid && (
                <div>
                  <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Winning Bid</div>
                  <div style={{ color: C.white, fontWeight: 700, fontSize: 20 }}>${outcome.winning_bid.toLocaleString()}</div>
                </div>
              )}
              <div>
                <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Total Recovered</div>
                {isEditing ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ color: C.grey, fontSize: 14 }}>$</span>
                    <input type="number" inputMode="decimal" placeholder="0.00"
                      value={phase3Input.totalSold}
                      onChange={(e) => setPhase3Input(p => ({ ...p, totalSold: e.target.value, dirty: true }))}
                      style={{ ...S.input, fontSize: 18, fontWeight: 700, padding: "4px 8px", width: "100%" }} />
                  </div>
                ) : (
                  <div style={{ color: C.green, fontWeight: 700, fontSize: 20 }}>${outcome.phase3_total_sold?.toLocaleString()}</div>
                )}
              </div>
            </div>

            {/* Profit + ROI — live while typing, static when done */}
            {(() => {
              const recovered = isEditing ? parseFloat(phase3Input.totalSold) : outcome.phase3_total_sold;
              const bid = outcome.winning_bid;
              if (!bid || recovered == null || isNaN(recovered)) return null;
              const profit = recovered - bid;
              const roi = ((profit / bid) * 100).toFixed(0);
              const color = profit >= 0 ? C.green : C.red;
              return (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16, paddingTop: 4 }}>
                  <div>
                    <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Profit</div>
                    <div style={{ color, fontWeight: 700, fontSize: 20 }}>${profit.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>ROI</div>
                    <div style={{ color, fontWeight: 800, fontSize: 24 }}>{roi}%</div>
                  </div>
                </div>
              );
            })()}

            {/* Unit Turnover Time */}
            {isEditing && (
              <div style={{ marginBottom: 16, marginTop: 16 }}>
                <div style={{ color: C.grey, fontSize: 13, marginBottom: 8 }}>Unit Liquidation Time</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[{ value: "Under 1 week", label: "Under 1 wk" }, { value: "1–2 weeks", label: "1–2 wks" }, { value: "2–4 weeks", label: "2–4 wks" }, { value: "1–3 months", label: "1–3 mo" }].map(({ value, label }) => (
                    <button key={value} onClick={() => setPhase3Input(p => ({ ...p, liquidationTime: value, dirty: true }))}
                      style={{
                        flex: 1, padding: "8px 4px", borderRadius: 8, fontSize: 11, textAlign: "center",
                        border: `1px solid ${phase3Input.liquidationTime === value ? C.green : C.border}`,
                        background: phase3Input.liquidationTime === value ? `${C.green}20` : C.bgDeep,
                        color: phase3Input.liquidationTime === value ? C.green : C.grey,
                        cursor: "pointer", fontWeight: phase3Input.liquidationTime === value ? 600 : 400,
                      }}>{label}</button>
                  ))}
                </div>
              </div>
            )}
            {!isEditing && outcome.phase3_liquidation_time && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                <div style={{ color: C.grey, fontSize: 12, marginBottom: 2 }}>Unit Liquidation Time</div>
                <div style={{ color: C.white, fontWeight: 600, fontSize: 13 }}>{outcome.phase3_liquidation_time}</div>
              </div>
            )}

            {isEditing && phase3Input.saving && (
              <div style={{ color: C.grey, fontSize: 12, textAlign: "center", marginTop: 8 }}>Saving...</div>
            )}
            {isEditing && !phase3Input.saving && (
              <button onClick={handlePhase3Save} disabled={!phase3CanSave}
                style={{ width: "100%", padding: "13px", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: phase3CanSave ? "pointer" : "not-allowed", border: "none", background: phase3CanSave ? C.green : `${C.green}25`, color: phase3CanSave ? C.white : `${C.green}60`, marginTop: 8, transition: "all 0.15s" }}>
                Save Closing Data
              </button>
            )}
            </>); })()}
          </div>
        )}

        {/* Edit closing data link — only shown when phase3 complete */}
        {outcome?.outcome_type === "won" && !phase3Pending && !showEditClosing && (
          <div style={{ textAlign: "center", marginBottom: 16, marginTop: -8 }}>
            <button onClick={() => {
              setPhase3Input({
                totalSold: outcome.phase3_total_sold?.toString() || "",
                liquidationTime: outcome.phase3_liquidation_time || "",
                saving: false,
                dirty: false,
              });
              setShowEditClosing(true);
            }} style={{ background: "none", border: "none", color: C.grey, fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>
              Edit closing data
            </button>
          </div>
        )}

        {/* Lost bid data */}
        {outcome?.outcome_type === "lost" && outcome.final_bid_lost && (
          <div style={{ ...S.card, marginBottom: 16, border: `1px solid ${C.border}` }}>
            <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Your Bid</div>
            <div style={{ color: C.white, fontWeight: 700, fontSize: 20 }}>${outcome.final_bid_lost.toLocaleString()}</div>
          </div>
        )}

        {/* 3. Photos */}
        <PhotoCarousel
          photos={selectedScan.photo_urls}
          onSetPrimary={selectedScan.photo_urls?.length > 1 ? handleSetPrimary : null}
        />

        {/* Change outcome */}
        <div style={{ marginBottom: 16 }}>
          {!showChangeOutcome ? (
            <div style={{ textAlign: "center" }}>
              <button onClick={() => setShowChangeOutcome(true)}
                style={{ background: "none", border: "none", color: C.grey, fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>
                Change outcome
              </button>
            </div>
          ) : (
            <OutcomeSection
              scanId={selectedScan.id}
              userId={user.id}
              existingOutcome={outcome}
              existingCategory={selectedScan.user_category}
              isOnlineBidder={user?.isOnlineBidder}
              onOutcomeSet={(type, cat) => {
                setOutcomes((prev) => ({
                  ...prev,
                  [selectedScan.id]: { ...prev[selectedScan.id], outcome_type: type, scan_id: selectedScan.id, user_id: user.id }
                }));
                if (cat) {
                  setScans((prev) => prev.map((s) => s.id === selectedScan.id ? { ...s, user_category: cat } : s));
                  setSelectedScan((prev) => ({ ...prev, user_category: cat }));
                }
                setShowChangeOutcome(false);
              }}
            />
          )}
        </div>

        {/* Delete — bottom, intentional scroll */}
        <div style={{ marginTop: 40, paddingTop: 24, borderTop: `1px solid ${C.border}`, textAlign: "center" }}>
          <button
            onClick={async () => {
              if (!window.confirm("Are you sure? This cannot be undone.")) return;
              setDeletingId(selectedScan.id);
              await supabase.from("logs").delete().eq("id", selectedScan.id).eq("user_id", user.id);
              setScans((prev) => prev.filter((s) => s.id !== selectedScan.id));
              setSelectedScan(null);
              setDeletingId(null);
            }}
            disabled={deletingId === selectedScan.id}
            style={{ background: "none", border: `1px solid ${C.red}60`, color: C.red, borderRadius: 8, padding: "10px 24px", fontSize: 13, cursor: "pointer", opacity: 0.7 }}>
            {deletingId === selectedScan.id ? "Deleting..." : "Delete Unit"}
          </button>
        </div>
      </div>
    );
  }


  return (
    <div>

      {/* Share modal — visual card */}
      {shareCardId && (() => {
        const sc = scans.find(s => s.id === shareCardId);
        const o = outcomes[shareCardId];
        if (!sc) return null;
        const outcomeLabel = { won: "Won", lost: "Lost", passed: "Passed", still_bidding: "Watching" }[o?.outcome_type] || "Pending";
        const outcomeColor = { won: "#22c55e", lost: "#ef4444", passed: "#9CA3AF", still_bidding: "#eab308" }[o?.outcome_type] || "#60a5fa";
        const facility = sc.facility_name || (sc.location_city ? [sc.location_city, sc.location_state].filter(Boolean).join(", ") : "");
        const roi = o?.phase3_roi;
        const bid = o?.winning_bid;
        const recovered = o?.phase3_total_sold;
        const profit = recovered != null && bid != null ? recovered - bid : null;
        const platforms = o?.phase3_sell_platforms;
        const liqTime = o?.phase3_liquidation_time;
        const fmtD = (v) => v == null ? null : `$${parseFloat(v).toLocaleString()}`;

        const buildCard = (inc) => `
          <div style="background:#13151F;border:1px solid rgba(255,255,255,0.08);border-radius:14px;padding:22px 24px;font-family:Georgia,serif;color:#fff;width:360px;box-sizing:border-box;">
            <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:14px;">
              <div>
                <div style="font-size:20px;font-weight:700;margin-bottom:4px;">${sc.unit_label || "Unit"}</div>
                <div style="color:#9CA3AF;font-size:12px;">${sc.date}${facility ? " · " + facility : ""}</div>
                ${sc.user_category ? `<div style="color:#F59E0B;font-size:12px;margin-top:2px;">${sc.user_category}</div>` : ""}
              </div>
              <span style="background:${outcomeColor}20;color:${outcomeColor};border:1px solid ${outcomeColor}40;border-radius:20px;padding:3px 12px;font-size:12px;font-weight:700;white-space:nowrap;margin-left:12px;">${outcomeLabel}</span>
            </div>
            ${inc && (bid || recovered != null) ? `
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px;">
              ${bid ? `<div><div style="color:#9CA3AF;font-size:11px;margin-bottom:2px;">Winning Bid</div><div style="font-weight:700;font-size:17px;">${fmtD(bid)}</div></div>` : ""}
              ${recovered != null ? `<div><div style="color:#9CA3AF;font-size:11px;margin-bottom:2px;">Recovered</div><div style="color:#22c55e;font-weight:700;font-size:17px;">${fmtD(recovered)}</div></div>` : ""}
              ${profit != null ? `<div><div style="color:#9CA3AF;font-size:11px;margin-bottom:2px;">Profit</div><div style="color:${profit >= 0 ? "#22c55e" : "#ef4444"};font-weight:700;font-size:17px;">${fmtD(profit)}</div></div>` : ""}
              ${roi != null ? `<div><div style="color:#9CA3AF;font-size:11px;margin-bottom:2px;">ROI</div><div style="color:${roi >= 0 ? "#22c55e" : "#ef4444"};font-weight:800;font-size:20px;">${roi}%</div></div>` : ""}
            </div>` : ""}
            ${liqTime || platforms ? `<div style="display:flex;gap:20px;border-top:1px solid rgba(255,255,255,0.07);padding-top:12px;">
              ${liqTime ? `<div><div style="color:#9CA3AF;font-size:11px;margin-bottom:2px;">Unit Turnover</div><div style="font-weight:600;font-size:13px;">${liqTime}</div></div>` : ""}
              ${platforms ? `<div><div style="color:#9CA3AF;font-size:11px;margin-bottom:2px;">Sold On</div><div style="font-weight:600;font-size:13px;">${platforms}</div></div>` : ""}
            </div>` : ""}
            <div style="text-align:center;color:#4B5563;font-size:10px;margin-top:16px;letter-spacing:1.5px;text-transform:uppercase;">myunitscout.com</div>
          </div>`;

        const openWindow = (inc) => {
          const w = window.open("", "_blank");
          w.document.write(`<!DOCTYPE html><html><head><title>UnitScout</title><style>
            body{margin:0;background:#0D0F1A;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:24px;box-sizing:border-box;gap:16px;}
            button{padding:12px 28px;border-radius:10px;border:none;background:#F59E0B;color:#000;font-size:15px;font-weight:700;cursor:pointer;}
            p{color:#9CA3AF;font-size:13px;margin:0;}
            @media print{button,p{display:none}}
          </style></head><body>
            ${buildCard(inc)}
            <p>Screenshot or print this page to save</p>
            <button onclick="window.print()">Save / Print</button>
          </body></html>`);
          w.document.close();
        };

        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 500, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
            onClick={() => setShareCardId(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: C.bgCard, borderRadius: "16px 16px 0 0", padding: 24, width: "100%", maxWidth: 480, boxSizing: "border-box" }}>
              <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Share {sc.unit_label || "Unit"}</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, padding: "10px 14px", background: C.bgDeep, borderRadius: 10 }}>
                <span style={{ color: C.grey, fontSize: 13 }}>Include financial data</span>
                <div onClick={() => setShareIncludeFinancials(p => !p)} style={{ width: 40, height: 22, borderRadius: 11, background: shareIncludeFinancials ? C.gold : C.border, cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: C.white, position: "absolute", top: 2, left: shareIncludeFinancials ? 20 : 2, transition: "left 0.2s" }} />
                </div>
              </div>
              <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}
                dangerouslySetInnerHTML={{ __html: buildCard(shareIncludeFinancials) }} />
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { openWindow(shareIncludeFinancials); setShareCardId(null); }}
                  style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: C.gold, color: C.bg, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  Open Card
                </button>
                <button onClick={() => {
                  const t = [sc.unit_label || "Unit", outcomeLabel, facility, roi != null && shareIncludeFinancials ? `${roi}% ROI` : null, "myunitscout.com"].filter(Boolean).join(" · ");
                  window.open(`sms:?body=${encodeURIComponent(t)}`); setShareCardId(null);
                }} style={{ flex: 1, padding: "12px", borderRadius: 10, border: `1px solid ${C.border}`, background: "none", color: C.grey, fontSize: 14, cursor: "pointer" }}>
                  Text
                </button>
              </div>
              <button onClick={() => setShareCardId(null)} style={{ width: "100%", marginTop: 10, padding: "10px", border: "none", background: "none", color: C.grey, fontSize: 13, cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        );
      })()}

      {/* Sticky filter bar */}
      <div style={{
        position: "sticky", top: 56, zIndex: 99,
        background: C.bg,
        padding: "10px 0 8px",
        marginBottom: 12,
        borderBottom: `1px solid ${C.border}`,
      }}>
        {/* Primary filter tabs */}
        {scans.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingBottom: 4, marginBottom: 8 }}>
            {[
              { id: "all", label: "All", color: C.white, dot: null },
              { id: "won", label: "Won", color: C.green, dot: C.green },
              { id: "passed", label: "Passed", color: C.grey, dot: "#9CA3AF" },
              ...(user?.isOnlineBidder ? [{ id: "still_bidding", label: "Watching", color: C.yellow, dot: C.yellow }] : []),
              { id: "pending", label: "Pending", color: C.blue, dot: C.blue, count: scans.filter(s => {
                const o = outcomes[s.id];
                if (!o) return true;
                if (o.outcome_type !== "won") return false;
                return !o.phase3_complete;
              }).length },
            ].map(({ id, label, color, dot, count }) => {
              const isActive = filter === id;
              return (
                <button key={id} onClick={() => { setFilter(id); setCurrentPage(1); }}
                  style={{
                    padding: "7px 13px", borderRadius: 20, whiteSpace: "nowrap",
                    border: `1px solid ${isActive ? color : C.border}`,
                    background: isActive ? color : C.bgDeep,
                    color: isActive ? C.bg : C.grey,
                    fontSize: 13, cursor: "pointer", fontWeight: isActive ? 700 : 400,
                    display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
                  }}>
                  {dot && <span style={{ width: 9.2, height: 9.2, borderRadius: "50%", background: isActive ? `${dot}cc` : dot, border: isActive ? "1.5px solid rgba(0,0,0,0.3)" : "none", display: "inline-block", flexShrink: 0 }} />}
                  {label}
                  {count > 0 && (
                    <span style={{
                      background: isActive ? `${C.bg}30` : `${C.blue}25`,
                      color: isActive ? C.bg : C.blue,
                      borderRadius: 20, fontSize: 11, fontWeight: 700,
                      padding: "1px 7px", lineHeight: 1.4,
                    }}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* More filters + Search row */}
        {scans.length > 0 && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1 }}>
            <button onClick={() => setShowMoreFilters(!showMoreFilters)}
              style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 13px", color: C.grey, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
              {showMoreFilters ? "▲ Hide filters" : "▼ More filters"}
            </button>

            {showMoreFilters && (
              <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", marginTop: 10 }}>

                {/* Sort By */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ color: C.grey, fontSize: 10, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 7 }}>Sort By</div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap", paddingBottom: 2 }}>
                    {[
                      { value: "date_desc", label: "Newest First" },
                      { value: "date_asc", label: "Oldest First" },
                      { value: "bid_desc", label: "Winning Bid: High to Low" },
                      { value: "roi_desc", label: "ROI: High to Low" },
                      { value: "liq_time_asc", label: "Unit Turnover: Fastest First" },
                      { value: "location_az", label: "Facility: A to Z" },
                    ].map(({ value, label }) => (
                      <button key={value} onClick={() => setSortBy(value)}
                        style={{
                          padding: "5px 11px", borderRadius: 20, fontSize: 11, whiteSpace: "nowrap", flexShrink: 0,
                          border: `1px solid ${sortBy === value ? C.gold : C.border}`,
                          background: sortBy === value ? `${C.gold}20` : C.bgDeep,
                          color: sortBy === value ? C.gold : C.grey, cursor: "pointer",
                          fontWeight: sortBy === value ? 600 : 400,
                        }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Auction Type — only show if online bidder */}
                {user?.isOnlineBidder && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ color: C.grey, fontSize: 10, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 7 }}>Auction Type</div>
                  <div style={{ display: "flex", gap: 5 }}>
                    {[
                      { value: "all", label: "All" },
                      { value: "in_person", label: "In-Person" },
                      { value: "online", label: "Online" },
                    ].map(({ value, label }) => (
                      <button key={value} onClick={() => setFilterAuctionType(value)}
                        style={{
                          padding: "5px 11px", borderRadius: 20, fontSize: 11,
                          border: `1px solid ${filterAuctionType === value ? C.gold : C.border}`,
                          background: filterAuctionType === value ? `${C.gold}20` : C.bgDeep,
                          color: filterAuctionType === value ? C.gold : C.grey, cursor: "pointer",
                          fontWeight: filterAuctionType === value ? 600 : 400,
                        }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                )}

                {/* Date Range */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ color: C.grey, fontSize: 10, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 7 }}>Date Range</div>
                  <div style={{ display: "flex", gap: 5 }}>
                    {[
                      { value: "all", label: "All" },
                      { value: "7d", label: "7D" },
                      { value: "30d", label: "30D" },
                      { value: "90d", label: "90D" },
                      { value: "6m", label: "6M" },
                    ].map(({ value, label }) => (
                      <button key={value} onClick={() => setFilterDateRange(value)}
                        style={{
                          padding: "5px 11px", borderRadius: 20, fontSize: 11,
                          border: `1px solid ${filterDateRange === value ? C.gold : C.border}`,
                          background: filterDateRange === value ? `${C.gold}20` : C.bgDeep,
                          color: filterDateRange === value ? C.gold : C.grey, cursor: "pointer",
                          fontWeight: filterDateRange === value ? 600 : 400,
                        }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Facility */}
                {(() => {
                  const facilities = [...new Set(scans.map(s => s.facility_name).filter(Boolean))].sort();
                  if (facilities.length === 0) return null;
                  return (
                    <div>
                      <div style={{ color: C.grey, fontSize: 10, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 7 }}>Facility</div>
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          placeholder="Search facilities..."
                          value={facilitySearch}
                          onChange={e => setFacilitySearch(e.target.value)}
                          style={{
                            width: "100%", background: C.bgDeep, border: `1px solid ${filterFacility !== "all" ? C.gold : C.border}`,
                            borderRadius: 8, padding: "7px 12px", color: C.white, fontSize: 12,
                            boxSizing: "border-box", outline: "none",
                          }}
                        />
                        {filterFacility !== "all" && (
                          <button onClick={() => { setFilterFacility("all"); setFacilitySearch(""); }}
                            style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.grey, cursor: "pointer", fontSize: 14, padding: 0 }}>
                            ✕
                          </button>
                        )}
                      </div>
                      {facilitySearch && (
                        <div style={{ background: C.bgDeep, border: `1px solid ${C.border}`, borderRadius: 8, marginTop: 4, maxHeight: 180, overflowY: "auto" }}>
                          {facilities.filter(f => f.toLowerCase().includes(facilitySearch.toLowerCase())).map(f => (
                            <div key={f} onClick={() => { setFilterFacility(f); setFacilitySearch(f); }}
                              style={{
                                padding: "8px 12px", fontSize: 12, cursor: "pointer",
                                color: filterFacility === f ? C.gold : C.white,
                                background: filterFacility === f ? `${C.gold}15` : "transparent",
                                borderBottom: `1px solid ${C.border}`,
                              }}>
                              {f}
                            </div>
                          ))}
                          {facilities.filter(f => f.toLowerCase().includes(facilitySearch.toLowerCase())).length === 0 && (
                            <div style={{ padding: "8px 12px", fontSize: 12, color: C.grey }}>No facilities found</div>
                          )}
                        </div>
                      )}
                      {filterFacility !== "all" && (
                        <div style={{ marginTop: 6, fontSize: 11, color: C.gold }}>
                          Filtered: {filterFacility}
                        </div>
                      )}
                    </div>
                  );
                })()}

              </div>
            )}
            </div>
          </div>
        )}
      </div>

      {/* Active filter chips */}
      {(filterDateRange !== "all" || filterAuctionType !== "all" || filterFacility !== "all") && (
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", paddingTop: 8 }}>
          {filterDateRange !== "all" && (
            <span onClick={() => setFilterDateRange("all")}
              style={{ background: `${C.gold}20`, color: C.gold, fontSize: 12, padding: "4px 10px", borderRadius: 20, cursor: "pointer" }}>
              {filterDateRange.toUpperCase()} ✕
            </span>
          )}
          {filterAuctionType !== "all" && (
            <span onClick={() => setFilterAuctionType("all")}
              style={{ background: `${C.gold}20`, color: C.gold, fontSize: 12, padding: "4px 10px", borderRadius: 20, cursor: "pointer" }}>
              {filterAuctionType === "online" ? "Online" : "In-Person"} ✕
            </span>
          )}
        </div>
      )}

      {/* ROI sort — no data nudge */}
      {filteredScans.length === 0 && sortBy === "roi_desc" && (() => {
        const firstWonMissingPhase3 = scans.find((s) => {
          const o = outcomes[s.id];
          return o?.outcome_type === "won" && !o?.phase3_complete;
        });
        return (
          <div onClick={firstWonMissingPhase3 ? () => openScan(firstWonMissingPhase3) : undefined}
            style={{ ...S.card, textAlign: "center", padding: "32px 24px", border: `1px solid ${C.border}`, cursor: firstWonMissingPhase3 ? "pointer" : "default" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
            <div style={{ color: C.white, fontWeight: 700, fontSize: 15, marginBottom: 8 }}>No ROI data yet.</div>
            <div style={{ color: C.grey, fontSize: 14, lineHeight: 1.6 }}>Add liquidation details to your Won units to see ROI rankings.</div>
            {firstWonMissingPhase3 && (
              <div style={{ color: C.gold, fontSize: 13, marginTop: 14, fontWeight: 600 }}>Tap to add results to your first won unit →</div>
            )}
          </div>
        );
      })()}

      {/* Regular empty state */}
      {sortBy !== "roi_desc" && filteredScans.length === 0 && (
        <div style={{ ...S.card, textAlign: "center", padding: 60 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <Camera size={48} color={C.grey} strokeWidth={1.5} />
          </div>
          <h3 style={{ color: C.white, marginBottom: 8 }}>
            {scans.length === 0 ? "No units yet" : "No units match this filter"}
          </h3>
          <p style={{ color: C.grey, fontSize: 14, marginBottom: scans.length === 0 ? 24 : 0 }}>
            {scans.length === 0 ? "Log your first unit to start building your history." : "Try a different filter."}
          </p>
          {scans.length === 0 && (
            <button onClick={() => {
              if (onChangePage) onChangePage("scanner");
              setTimeout(() => window.dispatchEvent(new CustomEvent("unitscout-open-camera")), 100);
            }} style={{ ...S.goldBtn, fontSize: 15, padding: "12px 32px" }}>
              Log Unit
            </button>
          )}
        </div>
      )}

      {/* History cards — 2-col on desktop */}
      <div className="history-grid">
      {pagedScans.map((scan) => {
        const outcome = outcomes[scan.id];
        const noOutcome = !outcome;
        const hasPhase3Pending = outcome?.outcome_type === "won" && !outcome?.phase3_complete;
        const hasRoi = outcome?.phase3_roi != null;
        const hasTurnover = outcome?.phase3_liquidation_time != null;
        const hasWinBid = outcome?.winning_bid != null;
        const showRoiHint = sortBy === "roi_desc";
        const showTurnoverHint = sortBy === "liq_time_asc";
        const showBidHint = sortBy === "bid_desc";
        const photos = scan.photo_urls || [];
        const imgIdx = cardImageIndex[scan.id] || 0;

        // Status dot only - no text
        const getDot = () => {
          if (!outcome) return { color: C.blue }; // no outcome = blue pending
          if (outcome.outcome_type === "won" && !outcome.phase3_complete) return { color: C.blue }; // won pending
          const dotColors = { won: C.green, lost: C.red, passed: "#9CA3AF", still_bidding: C.yellow };
          return { color: dotColors[outcome.outcome_type] || C.grey };
        };
        const dot = getDot();

        return (
          <div key={scan.id} className="history-card"
            style={{ boxShadow: (showRoiHint && hasRoi) || (showTurnoverHint && hasTurnover) || (showBidHint && hasWinBid) ? `0 0 16px ${C.gold}25` : "none", position: "relative" }}>
            {/* Header — title left, status + share right */}
            <div onClick={() => openScan(scan)} style={{ padding: "10px 14px 8px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, cursor: "pointer" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.white, fontSize: 18, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {scan.unit_label || "Unnamed Unit"}
                </div>
                {scan.facility_name && (
                  <div style={{ color: C.grey, fontSize: 12, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {scan.facility_name}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                {showRoiHint && hasRoi && (
                  <span style={{ background: `${C.green}20`, color: C.green, fontSize: 11, padding: "2px 9px", borderRadius: 20, fontWeight: 700 }}>
                    {outcome.phase3_roi}% ROI
                  </span>
                )}
                {showTurnoverHint && hasTurnover && (
                  <span style={{ background: `${C.gold}20`, color: C.gold, fontSize: 11, padding: "2px 9px", borderRadius: 20, fontWeight: 700 }}>
                    {outcome.phase3_liquidation_time}
                  </span>
                )}
                {showBidHint && hasWinBid && (
                  <span style={{ background: `${C.gold}20`, color: C.gold, fontSize: 11, padding: "2px 9px", borderRadius: 20, fontWeight: 700 }}>
                    ${outcome.winning_bid.toLocaleString()}
                  </span>
                )}
                <span style={{ width: 9.2, height: 9.2, borderRadius: "50%", background: dot.color, display: "inline-block", flexShrink: 0 }} />
              </div>
            </div>

            {/* Image area — swipeable/scrollable */}
            {photos.length > 0 ? (
              <div style={{ position: "relative", userSelect: "none" }}
                onTouchStart={(e) => setDragStart(prev => ({ ...prev, [scan.id]: e.touches[0].clientX }))}
                onTouchEnd={(e) => {
                  const startX = dragStart[scan.id];
                  if (startX == null) return;
                  const diff = startX - e.changedTouches[0].clientX;
                  if (Math.abs(diff) > 40) setCardImg(scan.id, imgIdx + (diff > 0 ? 1 : -1), photos.length);
                  setDragStart(prev => ({ ...prev, [scan.id]: null }));
                }}
                onMouseDown={(e) => setDragStart(prev => ({ ...prev, [scan.id]: e.clientX }))}
                onMouseUp={(e) => {
                  const startX = dragStart[scan.id];
                  if (startX == null) return;
                  const diff = startX - e.clientX;
                  if (Math.abs(diff) > 40) setCardImg(scan.id, imgIdx + (diff > 0 ? 1 : -1), photos.length);
                  setDragStart(prev => ({ ...prev, [scan.id]: null }));
                }}
                onClick={(e) => {
                  if (Math.abs((dragStart[scan.id] || e.clientX) - e.clientX) < 8) openScan(scan);
                }}>
                <img src={photos[imgIdx]} alt="Unit"
                  style={{ width: "100%", height: 210, objectFit: "cover", display: "block", pointerEvents: "none" }} />

                {/* Prev/Next arrows — desktop */}
                {photos.length > 1 && imgIdx > 0 && (
                  <button onClick={(e) => { e.stopPropagation(); setCardImg(scan.id, imgIdx - 1, photos.length); }}
                    style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", background: "#00000070", border: "none", borderRadius: "50%", width: 32, height: 32, color: C.white, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    ‹
                  </button>
                )}
                {photos.length > 1 && imgIdx < photos.length - 1 && (
                  <button onClick={(e) => { e.stopPropagation(); setCardImg(scan.id, imgIdx + 1, photos.length); }}
                    style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "#00000070", border: "none", borderRadius: "50%", width: 32, height: 32, color: C.white, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    ›
                  </button>
                )}

                {/* Dot indicators */}
                {photos.length > 1 && (
                  <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 5 }}>
                    {photos.map((_, i) => (
                      <span key={i} onClick={(e) => { e.stopPropagation(); setCardImg(scan.id, i, photos.length); }}
                        style={{ width: 6, height: 6, borderRadius: "50%", background: i === imgIdx ? C.white : "#ffffff50", cursor: "pointer", display: "inline-block" }} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div onClick={() => openScan(scan)} style={{
                width: "100%", height: 120, background: C.bgDeep, display: "flex",
                flexDirection: "column", alignItems: "center", justifyContent: "center",
                cursor: "pointer", gap: 8,
              }}>
                <Camera size={28} color={C.border} strokeWidth={1.5} />
                <span style={{ color: C.border, fontSize: 12 }}>No photo</span>
              </div>
            )}
          </div>
        );
      })}
      </div> {/* end history-grid */}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, padding: "24px 0" }}>
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
            style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 14px", color: currentPage === 1 ? C.border : C.white, fontSize: 13, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}>
            ← Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button key={page} onClick={() => setCurrentPage(page)}
              style={{
                background: page === currentPage ? C.gold : "none",
                border: `1px solid ${page === currentPage ? C.gold : C.border}`,
                borderRadius: 8, padding: "6px 12px",
                color: page === currentPage ? C.bg : C.grey,
                fontSize: 13, cursor: "pointer", fontWeight: page === currentPage ? 700 : 400,
              }}>
              {page}
            </button>
          ))}
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
            style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 14px", color: currentPage === totalPages ? C.border : C.white, fontSize: 13, cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}>
            Next →
          </button>
        </div>
      )}

      {/* Share Options Modal */}
      {showShareOptions && selectedForShare && (() => {
        const scan = selectedForShare;
        const outcome = outcomes[scan.id];
        const photo = scan.photo_urls?.[0];
        const roi = outcome?.phase3_roi;
        const facility = scan.facility_name || scan.location_city || "";
        const outcomeLabel = { won: "Won", lost: "Lost", passed: "Passed", still_bidding: "Watching" }[outcome?.outcome_type] || "Pending";
        const dotColor = { won: "#22c55e", lost: "#ef4444", passed: "#9CA3AF", still_bidding: "#eab308" }[outcome?.outcome_type] || "#4A9EFF";
        const shareText = `UnitScout — ${scan.unit_label || "Unit"}${facility ? ` · ${facility}` : ""}${roi != null ? ` · ${roi}% ROI` : ""}\nmyunitscout.com`;
        return (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#000000CC", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 16 }}
            onClick={() => { setShowShareOptions(false); setShareMode(false); setSelectedForShare(null); }}>
            <div style={{ ...S.card, width: "100%", maxWidth: 480, marginBottom: 16 }} onClick={e => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div style={{ color: C.white, fontWeight: 700, fontSize: 16 }}>Share Unit</div>
                <button onClick={() => { setShowShareOptions(false); setShareMode(false); setSelectedForShare(null); }}
                  style={{ background: "none", border: "none", color: C.grey, fontSize: 20, cursor: "pointer" }}>✕</button>
              </div>
              {/* Preview */}
              <div style={{ background: C.bgDeep, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
                {photo && <img src={photo} alt="Unit" style={{ width: "100%", height: 160, objectFit: "cover", display: "block" }} />}
                <div style={{ padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor, display: "inline-block" }} />
                    <span style={{ color: C.white, fontWeight: 700, fontSize: 14 }}>{scan.unit_label || "Unnamed Unit"}</span>
                    <span style={{ color: dotColor, fontSize: 12, fontWeight: 600 }}>{outcomeLabel}</span>
                  </div>
                  {facility && <div style={{ color: C.grey, fontSize: 12 }}>{facility}</div>}
                  {roi != null && <div style={{ color: C.green, fontSize: 13, fontWeight: 700, marginTop: 4 }}>{roi}% ROI</div>}
                  <div style={{ color: C.border, fontSize: 10, marginTop: 6 }}>myunitscout.com</div>
                </div>
              </div>
              {/* Share channels */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                {[
                  { label: "Message", icon: <MessageSquare size={24} color={C.gold} strokeWidth={1.5} />, action: () => { window.open(`sms:?body=${encodeURIComponent(shareText)}`); } },
                  { label: "Facebook", icon: <Share2 size={24} color={C.gold} strokeWidth={1.5} />, action: () => { window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://myunitscout.com")}&quote=${encodeURIComponent(shareText)}`); } },
                  { label: "Email", icon: <Mail size={24} color={C.gold} strokeWidth={1.5} />, action: () => { window.open(`mailto:?subject=${encodeURIComponent("Check out this storage unit")}&body=${encodeURIComponent(shareText)}`); } },
                ].map(({ label, icon, action }) => (
                  <button key={label} onClick={action}
                    style={{ background: C.bgDeep, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 8px", cursor: "pointer", textAlign: "center" }}>
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>{icon}</div>
                    <div style={{ color: C.white, fontSize: 12 }}>{label}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// -- Insights Page -------------------------------------------------------------
function FacilityPerformanceCard({ facilities }) {
  const [sortBy, setSortBy] = useState("roi"); // "roi" | "turnover"
  const liqOrder = { "Under 1 week": 0, "1–2 weeks": 1, "2–4 weeks": 2, "1–3 months": 3, "Still selling": 4 };

  const sorted = [...facilities].sort((a, b) => {
    if (sortBy === "roi") {
      return (parseFloat(b.avgROI) || -999) - (parseFloat(a.avgROI) || -999);
    } else {
      return (liqOrder[a.avgTurnover] ?? 99) - (liqOrder[b.avgTurnover] ?? 99);
    }
  });

  return (
    <div style={{ ...S.card, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 24, height: 2, background: C.gold }} />
          <span style={{ color: C.gold, fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase" }}>Facility Performance</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setSortBy("roi")}
            style={{ padding: "4px 10px", borderRadius: 20, fontSize: 11, cursor: "pointer", border: `1px solid ${sortBy === "roi" ? C.gold : C.border}`, background: sortBy === "roi" ? `${C.gold}20` : "transparent", color: sortBy === "roi" ? C.gold : C.grey, fontWeight: sortBy === "roi" ? 700 : 400 }}>
            Avg ROI
          </button>
          <button onClick={() => setSortBy("turnover")}
            style={{ padding: "4px 10px", borderRadius: 20, fontSize: 11, cursor: "pointer", border: `1px solid ${sortBy === "turnover" ? C.gold : C.border}`, background: sortBy === "turnover" ? `${C.gold}20` : "transparent", color: sortBy === "turnover" ? C.gold : C.grey, fontWeight: sortBy === "turnover" ? 700 : 400 }}>
            Turnover
          </button>
        </div>
      </div>
      <div style={{ color: C.grey, fontSize: 10, marginBottom: 14, paddingLeft: 34 }}>
        Sorting by {sortBy === "roi" ? "highest avg ROI" : "fastest liquidation time"}
      </div>

      <div style={{ maxHeight: 420, overflowY: "auto" }}>
        {sorted.map((f, i) => {
          const roiNum = parseFloat(f.avgROI) || 0;
          return (
            <div key={f.name} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < sorted.length - 1 ? `1px solid ${C.border}` : "none" }}>
              {/* Left: name + address */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.white, fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
                {f.address && <div style={{ color: C.grey, fontSize: 11, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.address}</div>}
                <div style={{ color: C.grey, fontSize: 11, marginTop: 1 }}>{f.won} won · {f.visits} visit{f.visits !== 1 ? "s" : ""}</div>
              </div>
              {/* Right: AVG ROI + AVG Turnover */}
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <div style={{ background: C.bgDeep, borderRadius: 8, padding: "8px 12px", textAlign: "center", minWidth: 64 }}>
                  <div style={{ color: C.grey, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>Avg ROI</div>
                  <div style={{ color: roiNum > 0 ? C.gold : C.red, fontSize: 16, fontWeight: 800, lineHeight: 1 }}>{f.avgROI != null ? `${f.avgROI}%` : "—"}</div>
                </div>
                <div style={{ background: C.bgDeep, borderRadius: 8, padding: "8px 12px", textAlign: "center", minWidth: 80 }}>
                  <div style={{ color: C.grey, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>Avg Liquidation Time</div>
                  <div style={{ color: C.white, fontSize: 12, fontWeight: 600, lineHeight: 1.2 }}>{f.avgTurnover || "—"}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InsightsPage({ user, onChangePlan, showUpgradeModal, setShowUpgradeModal, onGoToHistory }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadInsights(); }, [user]);

  const loadInsights = async () => {
    if (!user?.id) { setLoading(false); return; }
    const { data: scans } = await supabase.from("logs").select("*").eq("user_id", user.id);
    let outcomeMap = {};
    (scans || []).forEach((log) => {
      outcomeMap[log.id] = {
        scan_id: log.id,
        user_id: log.user_id,
        outcome_type: log.outcome_type,
        winning_bid: log.winning_bid,
        phase3_complete: log.phase_complete,
        phase3_total_sold: log.total_recovered,
        phase3_roi: log.roi,
        phase3_liquidation_time: log.liquidation_time,
      };
    });
    const allScans = scans || [];
    const won = allScans.filter((s) => outcomeMap[s.id]?.outcome_type === "won");
    const lost = allScans.filter((s) => outcomeMap[s.id]?.outcome_type === "lost");
    const passed = allScans.filter((s) => outcomeMap[s.id]?.outcome_type === "passed");
    const winRate = won.length + passed.length > 0 ? ((won.length / (won.length + passed.length)) * 100).toFixed(0) : null;

    // Phase 2 completions
    const p2 = won.filter((s) => outcomeMap[s.id]?.phase3_complete);
    const p2Count = p2.length;

    const wonWithROI = p2.filter((s) => outcomeMap[s.id]?.phase3_roi != null);
    const avgROI = wonWithROI.length > 0 ? (wonWithROI.reduce((sum, s) => sum + outcomeMap[s.id].phase3_roi, 0) / wonWithROI.length).toFixed(0) : null;
    const totalProfit = p2.reduce((sum, s) => sum + ((outcomeMap[s.id]?.phase3_total_sold || 0) - (outcomeMap[s.id]?.winning_bid || 0)), 0);
    const totalRecovered = p2.reduce((sum, s) => sum + (outcomeMap[s.id]?.phase3_total_sold || 0), 0);

    // ROI trend - individual unit dots sorted by phase2_completed_at
    const roiUnits = wonWithROI.map((s) => {
      const o = outcomeMap[s.id];
      const completedAt = o?.phase3_completed_at || o?.outcome_recorded_at || s.created_at;
      return {
        date: new Date(completedAt),
        roi: o.phase3_roi,
        label: new Date(completedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        monthKey: `${new Date(completedAt).getFullYear()}-${String(new Date(completedAt).getMonth()+1).padStart(2,"0")}`,
        year: new Date(completedAt).getFullYear(),
        month: new Date(completedAt).getMonth() + 1,
      };
    }).sort((a, b) => a.date - b.date);

    // Monthly aggregated trend (for display)
    const monthMap = {};
    roiUnits.forEach(u => {
      if (!monthMap[u.monthKey]) monthMap[u.monthKey] = { rois: [], label: u.date.toLocaleDateString("en-US", { month: "short", year: "2-digit" }), monthKey: u.monthKey };
      monthMap[u.monthKey].rois.push(u.roi);
    });
    const roiTrend = Object.entries(monthMap).sort(([a],[b]) => a.localeCompare(b)).map(([,v]) => ({
      label: v.label, monthKey: v.monthKey,
      avgROI: parseFloat((v.rois.reduce((a,b) => a+b, 0) / v.rois.length).toFixed(1)),
    }));

    // Category stats - won only, ONLY user-assigned categories (skip Unknown/null)
    const VALID_CAT = (raw) => {
      if (!raw || raw === "Unknown") return null;
      if (raw.includes("/") || raw.toLowerCase().includes("boxes")) return "Miscellaneous";
      return raw;
    };
    const catMap = {};
    allScans.forEach((scan) => {
      const cat = VALID_CAT(scan.user_category);
      if (!cat) return; // skip unassigned
      const o = outcomeMap[scan.id];
      if (!catMap[cat]) catMap[cat] = { won: 0, lost: 0, passed: 0, roiTotal: 0, roiCount: 0, bids: [], liqTimes: [], platforms: [], sellThrough: [] };
      if (o?.outcome_type === "won") {
        catMap[cat].won++;
        if (o.winning_bid) catMap[cat].bids.push(o.winning_bid);
        if (o.phase3_roi != null) { catMap[cat].roiTotal += o.phase3_roi; catMap[cat].roiCount++; }
        if (o.phase3_liquidation_time) catMap[cat].liqTimes.push(o.phase3_liquidation_time);
        if (o.phase3_sell_platforms) o.phase3_sell_platforms.split(", ").forEach(p => catMap[cat].platforms.push(p));
        if (o.phase3_sell_through) catMap[cat].sellThrough.push(o.phase3_sell_through);
      }
      if (o?.outcome_type === "lost") catMap[cat].lost++;
      if (o?.outcome_type === "passed") catMap[cat].passed++;
    });
    const liqOrder = { "Under 1 week": 0, "1-2 weeks": 1, "1\u20132 weeks": 1, "2-4 weeks": 2, "2\u20134 weeks": 2, "1-3 months": 3, "1\u20133 months": 3, "Still selling": 4 };
    const categoryStats = Object.entries(catMap).filter(([,v]) => v.won > 0).map(([cat, v]) => {
      const winRateCat = v.won + v.lost > 0 ? ((v.won / (v.won + v.lost)) * 100).toFixed(0) : null;
      const topPlatform = v.platforms.length > 0 ? Object.entries(v.platforms.reduce((a,x) => ({...a,[x]:(a[x]||0)+1}),{})).sort((a,b)=>b[1]-a[1])[0][0] : null;
      const liqAvg = v.liqTimes.length > 0 ? v.liqTimes.reduce((sum, t) => sum + (liqOrder[t] ?? 99), 0) / v.liqTimes.length : null;
      const topLiq = v.liqTimes.length > 0 ? Object.entries(v.liqTimes.reduce((a,x)=>({...a,[x]:(a[x]||0)+1}),{})).sort((a,b)=>b[1]-a[1])[0][0] : null;
      const soldEverything = v.sellThrough.filter(s => s === "Sold everything").length;
      const sellThroughRate = v.sellThrough.length > 0 ? ((soldEverything / v.sellThrough.length) * 100).toFixed(0) : null;
      return {
        cat,
        won: v.won, lost: v.lost, passed: v.passed,
        avgROI: v.roiCount > 0 ? (v.roiTotal / v.roiCount).toFixed(0) : null,
        avgBid: v.bids.length > 0 ? (v.bids.reduce((a,b)=>a+b,0)/v.bids.length).toFixed(0) : null,
        topPlatform, winRateCat, liqAvg, topLiq, sellThroughRate,
      };
    }).sort((a,b) => (parseFloat(b.avgROI)||0) - (parseFloat(a.avgROI)||0));

    // Worst ROI single unit
    const worstROIUnit = wonWithROI.reduce((worst, s) => {
      const roi = outcomeMap[s.id]?.phase3_roi || 0;
      return roi < (outcomeMap[worst?.id]?.phase3_roi ?? Infinity) ? s : worst;
    }, null);
    const worstROI = worstROIUnit ? outcomeMap[worstROIUnit.id]?.phase3_roi : null;

    // Sell through rate overall
    const allSellThrough = p2.map(s => outcomeMap[s.id]?.phase3_sell_through).filter(Boolean);
    const sellThroughAll = allSellThrough.length > 0 ? ((allSellThrough.filter(s => s === "Sold everything").length / allSellThrough.length) * 100).toFixed(0) : null;

    // Units with no closing data (won but no phase3_total_sold)
    const wonNotRecovered = won.filter(s => outcomeMap[s.id]?.phase3_complete !== true).length;

    // Passed categories - only user-assigned categories on WON units (users don't assign cat on passed)
    // So we derive pass patterns from categories the user assigned on won units they've also passed on
    const passedCatMap = {};
    passed.forEach(s => {
      const cat = VALID_CAT(s.user_category);
      if (!cat) return;
      passedCatMap[cat] = (passedCatMap[cat] || 0) + 1;
    });
    const passedByCategory = Object.entries(passedCatMap).sort((a,b) => b[1]-a[1]);

    // Lost bids - only user-assigned categories (users don't assign cat on lost units by default)
    // Only show if user explicitly set a category on a lost unit
    const lostBidMap = {};
    lost.forEach(s => {
      const o = outcomeMap[s.id];
      if (!o?.final_bid_lost) return;
      const cat = VALID_CAT(s.user_category);
      if (!cat) return; // skip AI-only / unassigned
      if (!lostBidMap[cat]) lostBidMap[cat] = [];
      lostBidMap[cat].push(o.final_bid_lost);
    });
    const lostBidCeiling = Object.entries(lostBidMap).map(([cat, bids]) => ({
      cat, ceiling: Math.max(...bids), avg: (bids.reduce((a,b)=>a+b,0)/bids.length).toFixed(0), count: bids.length
    })).sort((a,b) => b.ceiling - a.ceiling);

    // Facility stats
    const facMap = {};
    allScans.forEach((scan) => {
      const key = scan.facility_name || (scan.online_auction ? "Online" : null);
      if (!key) return;
      if (!facMap[key]) facMap[key] = { name: key, address: scan.facility_address || null, visits: 0, won: 0, roiTotal: 0, roiCount: 0, liqTimes: [] };
      if (!facMap[key].address && scan.facility_address) facMap[key].address = scan.facility_address;
      facMap[key].visits++;
      const o = outcomeMap[scan.id];
      if (o?.outcome_type === "won") {
        facMap[key].won++;
        if (o.phase3_roi != null) { facMap[key].roiTotal += o.phase3_roi; facMap[key].roiCount++; }
        if (o.phase3_liquidation_time) facMap[key].liqTimes.push(o.phase3_liquidation_time);
      }
    });
    const liqOrderFac = { "Under 1 week": 0, "1–2 weeks": 1, "2–4 weeks": 2, "1–3 months": 3, "Still selling": 4 };
    const facilityStats = Object.values(facMap).map(f => {
      const avgROI = f.roiCount > 0 ? (f.roiTotal / f.roiCount).toFixed(0) : null;
      const topLiq = f.liqTimes.length > 0 ? Object.entries(f.liqTimes.reduce((a,x)=>({...a,[x]:(a[x]||0)+1}),{})).sort((a,b)=>b[1]-a[1])[0][0] : null;
      return { ...f, avgROI, avgTurnover: topLiq };
    });
    const facilityByVisits = [...facilityStats].sort((a,b) => b.visits - a.visits);
    const facilityByROI = [...facilityStats].filter(f => f.roiCount > 0).sort((a,b) => parseFloat(b.avgROI) - parseFloat(a.avgROI));

    // Win streak
    const sortedAll = [...allScans].sort((a,b) => new Date(a.created_at)-new Date(b.created_at));
    let streak = 0, best = 0, cur = 0;
    sortedAll.forEach(s => {
      if (outcomeMap[s.id]?.outcome_type === "won") { cur++; best = Math.max(best, cur); }
      else if (outcomeMap[s.id]?.outcome_type === "lost") cur = 0;
    });
    streak = best;

    // Platform stats
    const platMap = {};
    p2.forEach(s => {
      const o = outcomeMap[s.id];
      if (!o?.phase3_sell_platforms) return;
      o.phase3_sell_platforms.split(", ").forEach(p => {
        if (!platMap[p]) platMap[p] = { count: 0, liqTimes: [], catCounts: {} };
        platMap[p].count++;
        if (o.phase3_liquidation_time) platMap[p].liqTimes.push(o.phase3_liquidation_time);
        const cat = s.user_category;
        if (cat) platMap[p].catCounts[cat] = (platMap[p].catCounts[cat]||0)+1;
      });
    });
    const platformStats = Object.entries(platMap).map(([name, v]) => ({
      name, count: v.count,
      topLiq: v.liqTimes.length > 0 ? Object.entries(v.liqTimes.reduce((a,x)=>({...a,[x]:(a[x]||0)+1}),{})).sort((a,b)=>b[1]-a[1])[0][0] : null,
      bestCat: Object.entries(v.catCounts).sort((a,b)=>b[1]-a[1])[0]?.[0] || null,
    })).sort((a,b) => b.count - a.count);

    // Liq time avg
    const wonWithLiq = p2.filter(s => outcomeMap[s.id]?.phase3_liquidation_time);
    const avgLiqWeeks = wonWithLiq.length > 0 ? wonWithLiq.reduce((sum,s) => sum + (liqOrder[outcomeMap[s.id].phase3_liquidation_time]||0), 0) / wonWithLiq.length : null;
    const avgLiqLabel = avgLiqWeeks == null ? null : avgLiqWeeks < 1 ? "Under 1 week" : avgLiqWeeks < 2 ? "1–2 weeks" : avgLiqWeeks < 4 ? "2–4 weeks" : avgLiqWeeks < 10 ? "1–3 months" : "3+ months";

    // Box analysis
    const boxScans = p2.filter(s => outcomeMap[s.id]?.phase2_required);
    const noBoxScans = p2.filter(s => !outcomeMap[s.id]?.phase2_required);
    const boxROIs = boxScans.map(s => outcomeMap[s.id]?.phase3_roi).filter(r => r != null);
    const noBoxROIs = noBoxScans.map(s => outcomeMap[s.id]?.phase3_roi).filter(r => r != null);
    const boxAvgROI = boxROIs.length ? (boxROIs.reduce((a,b)=>a+b,0)/boxROIs.length).toFixed(0) : null;
    const noBoxAvgROI = noBoxROIs.length ? (noBoxROIs.reduce((a,b)=>a+b,0)/noBoxROIs.length).toFixed(0) : null;

    // Bid efficiency
    const allBids = won.map(s => outcomeMap[s.id]?.winning_bid).filter(Boolean);
    const totalSpent = allBids.reduce((a, b) => a + b, 0);
    const avgWinBid = allBids.length ? (totalSpent / allBids.length).toFixed(2) : null;
    const bestROI = wonWithROI.length > 0 ? Math.max(...wonWithROI.map(s => outcomeMap[s.id].phase3_roi)) : null;
    const bestProfit = p2.length > 0 ? Math.max(...p2.map(s => (outcomeMap[s.id]?.phase3_total_sold||0)-(outcomeMap[s.id]?.winning_bid||0))) : null;

    // Best single auction day profit
    const dayProfitMap = {};
    p2.forEach(s => {
      const o = outcomeMap[s.id];
      const profit = (o?.phase3_total_sold || 0) - (o?.winning_bid || 0);
      const day = (o?.phase3_completed_at || s.created_at).slice(0, 10);
      dayProfitMap[day] = (dayProfitMap[day] || 0) + profit;
    });
    const bestSingleDay = Object.values(dayProfitMap).length > 0 ? Math.max(...Object.values(dayProfitMap)) : null;

    setData({
      totalScans: allScans.length, won: won.length, lost: lost.length, passed: passed.length,
      winRate, avgROI, totalProfit: p2Count > 0 ? totalProfit : null,
      totalRecovered: p2Count > 0 ? totalRecovered : null,
      p2Count, roiTrend, roiUnits, categoryStats, passedByCategory, lostBidCeiling,
      facilityStats, facilityByVisits, facilityByROI,
      uniqueFacilityCount: Object.keys(facMap).length,
      platformStats, avgLiqLabel, boxAvgROI, noBoxAvgROI, boxCount: boxScans.length, noBoxCount: noBoxScans.length,
      totalSpent, avgWinBid, bestROI, bestProfit, bestSingleDay,
      worstROI, sellThroughAll, wonNotRecovered,
      pendingUnits: won.filter(s => !outcomeMap[s.id]?.phase3_complete).length,
    });
    setLoading(false);
  };

  if (loading) return <div style={{ textAlign: "center", color: C.grey, padding: 60 }}>Loading insights...</div>;
  if (!data || data.totalScans === 0) return (
    <div>
      <div style={{ ...S.card, textAlign: "center", padding: 60 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          <BarChart2 size={52} color={C.gold} strokeWidth={1.2} />
        </div>
        <p style={{ color: C.grey, fontSize: 14, margin: 0 }}>Your bidding intelligence grows with every unit you log.</p>
      </div>
    </div>
  );

  const n = data.totalScans;
  const p2 = data.p2Count;
  const allTimeAvgROI = data.avgROI ? parseFloat(data.avgROI) : null;
  const fmtMoney = (v) => v == null ? "—" : `$${parseFloat(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const SectionLabel = ({ label }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
      <div style={{ width: 24, height: 2, background: C.gold }} />
      <span style={{ color: C.gold, fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase" }}>{label}</span>
    </div>
  );

  const LockedCard = ({ title, needed }) => (
    <div style={{ ...S.card, marginBottom: 20, opacity: 0.7 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{ width: 24, height: 2, background: C.gold }} />
        <span style={{ color: C.gold, fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase" }}>{title}</span>
        <span style={{ color: C.gold, fontSize: 14, lineHeight: 1 }}>🔒</span>
      </div>
      <div style={{ color: C.grey, fontSize: 13 }}>
        Close out <span style={{ color: C.white, fontWeight: 600 }}>{needed} more won unit{needed !== 1 ? "s" : ""}</span> to unlock
      </div>
    </div>
  );

  // ROI Chart — individual unit dots connected by line
  const ROIChartWithFilter = ({ units, trend }) => {
    const [tFilter, setTFilter] = useState("all");
    const now = new Date();

    const getFiltered = () => {
      if (!units || units.length === 0) return [];
      if (tFilter === "all") return units;
      if (tFilter === "year") return units.filter(u => u.year === now.getFullYear());
      if (tFilter === "month") return units.filter(u => u.year === now.getFullYear() && u.month === now.getMonth() + 1);
      if (tFilter === "last10") return units.slice(-10);
      return units;
    };

    const filtered = getFiltered();
    const filteredAvg = filtered.length > 0 ? filtered.reduce((sum, u) => sum + u.roi, 0) / filtered.length : null;
    const headlineROI = filteredAvg != null ? filteredAvg.toFixed(0) : null;

    const W = 500, H = 140, pad = { t: 16, r: 16, b: 28, l: 42 };
    const cW = W - pad.l - pad.r, cH = H - pad.t - pad.b;

    const renderChart = () => {
      if (filtered.length === 0) return <div style={{ color: C.grey, fontSize: 13, padding: "16px 0" }}>No data for this period.</div>;
      const rois = filtered.map(u => u.roi);
      const minR = Math.min(...rois, allTimeAvgROI || rois[0]) - 15;
      const maxR = Math.max(...rois, allTimeAvgROI || rois[0]) + 15;
      const xS = (i) => filtered.length === 1 ? pad.l + cW / 2 : pad.l + (i / (filtered.length - 1)) * cW;
      const yS = (v) => pad.t + cH - ((v - minR) / (maxR - minR)) * cH;
      const line = filtered.length < 2 ? "" : filtered.map((u, i) => `${i === 0 ? "M" : "L"} ${xS(i)} ${yS(u.roi)}`).join(" ");
      const area = line ? `${line} L ${xS(filtered.length - 1)} ${pad.t + cH} L ${xS(0)} ${pad.t + cH} Z` : "";
      const avgY = allTimeAvgROI != null ? yS(allTimeAvgROI) : null;

      return (
        <div style={{ overflowX: "auto" }}>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, display: "block" }}>
            <defs>
              <linearGradient id="rg2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.gold} stopOpacity="0.2" />
                <stop offset="100%" stopColor={C.gold} stopOpacity="0.01" />
              </linearGradient>
            </defs>
            {[0, 0.5, 1].map(t => {
              const y = pad.t + t * cH;
              return <g key={t}><line x1={pad.l} y1={y} x2={W - pad.r} y2={y} stroke={C.border} strokeWidth="1" /><text x={pad.l - 5} y={y + 4} fill={C.grey} fontSize="9" textAnchor="end">{Math.round(maxR - t * (maxR - minR))}%</text></g>;
            })}
            {avgY != null && (
              <line x1={pad.l} y1={avgY} x2={W - pad.r} y2={avgY} stroke={C.gold} strokeWidth="1" strokeDasharray="4,3" opacity="0.5" />
            )}
            {area && <path d={area} fill="url(#rg2)" />}
            {line && <path d={line} fill="none" stroke={C.gold} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
            {filtered.map((u, i) => (
              <g key={i}>
                <circle cx={xS(i)} cy={yS(u.roi)} r="3.5" fill={C.gold} />
                {(filtered.length <= 6 || i === filtered.length - 1 || i === 0) && (
                  <text x={xS(i)} y={H - 4} fill={i === filtered.length - 1 ? C.gold : C.grey} fontSize="8" textAnchor="middle">{u.label}</text>
                )}
              </g>
            ))}
          </svg>
        </div>
      );
    };

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <div>
            <div style={{ color: C.gold, fontSize: 36, fontWeight: 800, lineHeight: 1 }}>{headlineROI != null ? `${headlineROI}%` : "—"}</div>
            <div style={{ color: C.grey, fontSize: 12, marginTop: 4 }}>avg ROI · {filtered.length} unit{filtered.length !== 1 ? "s" : ""}</div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[["all","All Time"],["year","This Year"],["month","This Month"],["last10","Last 10"]].map(([val, label]) => (
              <button key={val} onClick={() => setTFilter(val)} style={{ padding: "4px 10px", borderRadius: 20, fontSize: 11, cursor: "pointer", border: `1px solid ${tFilter === val ? C.gold : C.border}`, background: tFilter === val ? `${C.gold}20` : C.bgDeep, color: tFilter === val ? C.gold : C.grey, fontWeight: tFilter === val ? 600 : 400 }}>{label}</button>
            ))}
          </div>
        </div>
        {p2 === 0
          ? <div style={{ color: C.grey, fontSize: 13 }}>Close out your first won unit to start your trend line.</div>
          : renderChart()
        }
        {p2 >= 3 ? null : <div style={{ color: C.grey, fontSize: 11, marginTop: 10, fontStyle: "italic" }}>Each won unit you close adds a point to your trend.</div>}
        {allTimeAvgROI != null && tFilter !== "all" && (
          <div style={{ color: C.grey, fontSize: 11, marginTop: 8 }}>
            <span style={{ display: "inline-block", width: 20, borderTop: `1px dashed ${C.gold}`, verticalAlign: "middle", marginRight: 6 }} />
            All-time avg: {allTimeAvgROI}%
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {showUpgradeModal && <ChangePlanModal user={user} onClose={() => setShowUpgradeModal(false)} />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, margin: "0 0 4px", color: C.white }}>Your Insights</h2>
          <p style={{ color: C.grey, fontSize: 14, margin: 0 }}>Your bidding intelligence grows with every unit you log.</p>
        </div>
        <button onClick={() => {
          const w = window.open("", "_blank");
          const fmtM = (v) => v == null ? "—" : `$${parseFloat(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          const fmtK = (v) => v == null ? "—" : Math.abs(v) >= 1000 ? `$${(v/1000).toFixed(1)}k` : `$${parseInt(v)}`;
          const totalROI = data.totalProfit != null && data.totalSpent > 0 ? ((data.totalProfit / data.totalSpent) * 100).toFixed(0) : null;
          const catRows = data.categoryStats.map(c => `
            <div style="background:#1C1F2E;border:1px solid rgba(255,255,255,0.07);border-radius:10px;padding:14px 16px;">
              <div style="color:#9CA3AF;font-size:10px;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;">AVG ROI</div>
              <div style="color:#22c55e;font-size:20px;font-weight:800;margin-bottom:4px;">${c.avgROI ? c.avgROI + "%" : "—"}</div>
              <div style="color:#fff;font-size:12px;font-weight:600;">${c.cat}</div>
              <div style="color:#9CA3AF;font-size:11px;">${c.won} win${c.won !== 1 ? "s" : ""}</div>
            </div>`).join("");
          const facRows = data.facilityByROI.slice(0, 5).map((f, i) => {
            const medals = ["🥇","🥈","🥉"];
            return `<div style="display:flex;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid rgba(255,255,255,0.06);">
              <div style="width:26px;height:26px;border-radius:50%;background:rgba(245,158,11,0.15);border:1px solid rgba(245,158,11,0.4);display:flex;align-items:center;justify-content:center;font-size:13px;flex-shrink:0;">${medals[i] || i+1}</div>
              <div style="flex:1"><div style="color:#fff;font-weight:700;font-size:13px;">${f.name}</div><div style="color:#9CA3AF;font-size:11px;">${f.won} won · ${f.visits} visit${f.visits !== 1 ? "s" : ""}</div></div>
              <div style="color:#F59E0B;font-size:18px;font-weight:800;">${f.avgROI}%</div>
            </div>`;
          }).join("");

          w.document.write(`<!DOCTYPE html><html><head><title>UnitScout Insights</title><style>
            *{box-sizing:border-box;margin:0;padding:0}
            body{background:#0D0F1A;color:#fff;font-family:Georgia,serif;padding:32px;min-height:100vh}
            .card{background:#1C1F2E;border:1px solid rgba(255,255,255,0.07);border-radius:12px;padding:20px 24px;margin-bottom:16px}
            .label{color:#9CA3AF;font-size:10px;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px}
            .val{font-size:24px;font-weight:800;line-height:1}
            .sub{color:#9CA3AF;font-size:12px;margin-top:4px}
            .grid3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:16px}
            .grid2{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px}
            .gold{color:#F59E0B}.green{color:#22c55e}.blue{color:#60a5fa}
            .section-label{display:flex;align-items:center;gap:10px;margin-bottom:14px}
            .section-label-bar{width:24px;height:2px;background:#F59E0B}
            .section-label-text{color:#F59E0B;font-size:11px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase}
            .watermark{text-align:center;color:#374151;font-size:11px;letter-spacing:2px;text-transform:uppercase;margin-top:32px;padding-top:16px;border-top:1px solid rgba(255,255,255,0.06)}
            button{margin-top:20px;display:block;margin-left:auto;margin-right:auto;padding:12px 28px;border-radius:10px;border:none;background:#F59E0B;color:#000;font-size:15px;font-weight:700;cursor:pointer}
            @media print{button{display:none}}
          </style></head><body>

            <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;">
              <div>
                <div style="font-family:Georgia,serif;font-style:italic;font-size:22px;color:#fff;margin-bottom:4px;">UnitScout</div>
                <div style="color:#9CA3AF;font-size:13px;">Insights Export · ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
              </div>
            </div>

            <div class="grid3">
              <div class="card"><div class="label">Units Logged</div><div class="val" style="color:#fff">${data.totalScans}</div><div class="sub">${data.won}W · ${data.lost}L · ${data.passed}P${data.winRate ? " · " + data.winRate + "% win rate" : ""}</div></div>
              <div class="card"><div class="label">Total Profit</div><div class="val green">${fmtM(data.totalProfit)}</div><div class="sub">net profit</div></div>
              <div class="card"><div class="label">Avg ROI Per Unit</div><div class="val gold">${data.avgROI ? data.avgROI + "%" : "—"}</div><div class="sub">${p2} closed unit${p2 !== 1 ? "s" : ""}${totalROI ? " · " + totalROI + "% total" : ""}</div></div>
            </div>

            ${data.bestROI != null || data.bestProfit != null ? `
            <div class="card" style="margin-bottom:16px;">
              <div class="section-label"><div class="section-label-bar"></div><div class="section-label-text">Personal Bests</div></div>
              <div class="grid2">
                ${data.bestROI != null ? `<div style="background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.2);border-radius:10px;padding:14px"><div class="label">Best ROI</div><div class="val gold">${data.bestROI}%</div></div>` : ""}
                ${data.bestProfit != null ? `<div style="background:rgba(34,197,94,0.08);border:1px solid rgba(34,197,94,0.2);border-radius:10px;padding:14px"><div class="label">Best Single Unit Profit</div><div class="val green">${fmtK(data.bestProfit)}</div></div>` : ""}
              </div>
            </div>` : ""}

            ${catRows ? `
            <div class="card" style="margin-bottom:16px;">
              <div class="section-label"><div class="section-label-bar"></div><div class="section-label-text">Category Breakdown</div></div>
              <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:10px">${catRows}</div>
            </div>` : ""}

            ${data.facilityByROI.length > 0 ? `
            <div class="card" style="margin-bottom:16px;">
              <div class="section-label"><div class="section-label-bar"></div><div class="section-label-text">Facility Performance</div></div>
              ${facRows}
            </div>` : ""}

            <div class="watermark">myunitscout.com</div>
            <button onclick="window.print()">Save / Print</button>
          </body></html>`);
          w.document.close();
        }} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "7px 14px", color: C.grey, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, marginTop: 4 }}>
          ↓ Export PDF
        </button>
      </div>

      {/* -- HERO STATS: 3 cards -- */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
        {/* Card 1: Units Logged */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 18px" }}>
          <div style={{ color: C.grey, fontSize: 10, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>Units Logged</div>
          <div style={{ color: C.white, fontSize: 26, fontWeight: 800, lineHeight: 1, marginBottom: 8 }}>{n}</div>
          <div style={{ display: "flex", gap: 8, fontSize: 12 }}>
            <span style={{ color: C.green, fontWeight: 600 }}>{data.won} Won</span>
            <span style={{ color: C.grey }}>·</span>
            <span style={{ color: "#9CA3AF", fontWeight: 600 }}>{data.passed} Passed</span>
          </div>
          {data.winRate && <div style={{ color: C.grey, fontSize: 11, marginTop: 4 }}>Win Rate {data.winRate}%</div>}
        </div>

        {/* Card 2: Total Profit */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 18px" }}>
          <div style={{ color: C.grey, fontSize: 10, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>Total Profit</div>
          <div style={{ color: C.green, fontSize: 22, fontWeight: 800, lineHeight: 1, marginBottom: 8 }}>{data.totalProfit != null ? fmtMoney(data.totalProfit) : "—"}</div>
          {data.wonNotRecovered > 0 && (
            <div onClick={() => { if (onGoToHistory) onGoToHistory("pending"); }}
              style={{ color: C.gold, fontSize: 11, marginBottom: 8, padding: "8px 10px", background: `${C.gold}08`, borderRadius: 8, border: `1px solid ${C.gold}20`, lineHeight: 1.5, cursor: "pointer" }}>
              <span style={{ fontWeight: 700 }}>{data.wonNotRecovered} won unit{data.wonNotRecovered !== 1 ? "s" : ""} not yet closed out</span> — tap to complete →
            </div>
          )}
          {data.totalRecovered != null && (
            <div style={{ display: "flex", flexDirection: "column", gap: 3, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: C.grey, fontSize: 11 }}>Recovered</span>
                <span style={{ color: C.white, fontSize: 11, fontWeight: 600 }}>{fmtMoney(data.totalRecovered)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: C.grey, fontSize: 11 }}>Invested</span>
                <span style={{ color: C.white, fontSize: 11, fontWeight: 600 }}>{data.totalSpent > 0 ? fmtMoney(data.totalSpent) : "—"}</span>
              </div>
            </div>
          )}
        </div>

        {/* Card 3: ROI Overview — 4 quadrant */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 18px" }}>
          <div style={{ color: C.grey, fontSize: 10, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 12 }}>ROI Overview</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={{ color: C.grey, fontSize: 10, marginBottom: 3 }}>Avg ROI Per Unit</div>
              <div style={{ color: C.gold, fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{data.avgROI ? `${data.avgROI}%` : "—"}</div>
            </div>
            <div>
              <div style={{ color: C.grey, fontSize: 10, marginBottom: 3 }}>Best Single Unit ROI</div>
              <div style={{ color: C.green, fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{data.bestROI != null ? `${data.bestROI}%` : "—"}</div>
            </div>
            <div>
              <div style={{ color: C.grey, fontSize: 10, marginBottom: 3 }}>Avg Liquidation Time</div>
              <div style={{ color: C.white, fontSize: data.avgLiqLabel ? 14 : 22, fontWeight: 800, lineHeight: 1 }}>{data.avgLiqLabel || "—"}</div>
            </div>
            <div>
              <div style={{ color: C.grey, fontSize: 10, marginBottom: 3 }}>Best Single Unit Profit</div>
              <div style={{ color: C.green, fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{data.bestProfit != null ? fmtMoney(data.bestProfit) : "—"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* -- FACILITY PERFORMANCE -- */}
      {(() => {
        const facilitiesWithData = (data.facilityStats || []).filter(f => f.won > 0);
        if (facilitiesWithData.length === 0) {
          return (
            <div style={{ ...S.card, marginBottom: 20 }}>
              <SectionLabel label="Facility Performance" />
              <div style={{ color: C.grey, fontSize: 13 }}>Close out your first won unit to start building facility data.</div>
            </div>
          );
        }
        return <FacilityPerformanceCard facilities={facilitiesWithData} />;
      })()}
    </div>
  );
}

// -- Plan Features -------------------------------------------------------------
function PlanFeatures({ plan }) {
  const features = {
    casual: ["30 scans/month", "6 photos per scan", "eBay sold listings for each item", "Outcome tracking", "Photo history", "Basic insights"],
    regular: ["150 scans/month", "6 photos per scan", "eBay sold listings for each item", "Outcome tracking", "Photo history", "Category insights + ROI", "Facility tracking"],
    professional: ["300 scans/month", "6 photos per scan", "eBay sold listings for each item", "Outcome tracking", "Photo history", "Complete insights + ROI", "Facility performance analytics"],
  };
  return (
    <div>
      {features[plan].map((f, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span style={{ color: C.gold, fontSize: 14 }}>✓</span>
          <span style={{ color: C.white, fontSize: 14 }}>{f}</span>
        </div>
      ))}
    </div>
  );
}

function ChangePlanModal({ user, onClose }) {
  const [selectedPlan, setSelectedPlan] = useState(user?.plan || "casual");
  const [loading, setLoading] = useState(false);

  const handleChangePlan = async () => {
    if (selectedPlan === user?.plan) { onClose(); return; }
    setLoading(true);
    const { data: profile } = await supabase.from("profiles").select("stripe_customer_id").eq("id", user.id).single();
    await redirectToCheckout(selectedPlan, user.id, user.email, profile?.stripe_customer_id || null);
    setLoading(false);
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#000000CC", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ ...S.card, maxWidth: 540, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, margin: 0 }}>Change Plan</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.grey, fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <p style={{ color: C.grey, fontSize: 14, marginBottom: 24 }}>
          Current plan: <span style={{ color: C.gold, fontWeight: 700 }}>{PLANS[user?.plan]?.name || "Casual"}</span>
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
          {["casual", "regular", "professional"].map((plan) => {
            const isCurrent = plan === user?.plan;
            const isSelected = plan === selectedPlan;
            return (
              <div key={plan} onClick={() => setSelectedPlan(plan)} style={{
                background: isSelected ? `${C.gold}15` : C.bgDeep,
                border: `2px solid ${isSelected ? C.gold : C.border}`,
                borderRadius: 12, padding: 20, cursor: "pointer",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${isSelected ? C.gold : C.border}`, background: isSelected ? C.gold : "transparent", flexShrink: 0 }} />
                    <span style={{ color: C.white, fontWeight: 700, fontSize: 16 }}>{PLANS[plan].name}</span>
                    {isCurrent && <span style={{ background: `${C.gold}30`, color: C.gold, fontSize: 11, padding: "2px 8px", borderRadius: 20 }}>Current</span>}
                  </div>
                  <span style={{ color: C.gold, fontWeight: 700, fontSize: 18 }}>
                    {PLANS[plan].price}<span style={{ color: C.grey, fontSize: 12, fontWeight: 400 }}>/mo</span>
                  </span>
                </div>
                <div style={{ paddingLeft: 28, color: C.grey, fontSize: 13 }}>{PLANS[plan].scans} scans/month</div>
              </div>
            );
          })}
        </div>
        <button onClick={handleChangePlan} disabled={loading || selectedPlan === user?.plan}
          style={{ ...S.goldBtn, width: "100%", opacity: (loading || selectedPlan === user?.plan) ? 0.5 : 1, cursor: (loading || selectedPlan === user?.plan) ? "not-allowed" : "pointer" }}>
          {loading ? "Redirecting..." : selectedPlan === user?.plan ? "Already on this plan" : `Switch to ${PLANS[selectedPlan].name} →`}
        </button>
      </div>
    </div>
  );
}

// -- Auth Modals ---------------------------------------------------------------
function ForgotPasswordPage({ onBack }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleReset = async () => {
    if (!email) { setError("Please enter your email."); return; }
    setLoading(true); setError("");
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: "https://myunitscout.com/?reset=true",
    });
    // Always show confirmation - never reveal if email exists
    setSent(true); setLoading(false);
  };

  if (sent) {
    return (
      <div style={{ ...S.page, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div style={{ width: "100%", maxWidth: 480, padding: "0 24px", boxSizing: "border-box", textAlign: "center" }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: 28, marginBottom: 48, cursor: "pointer", color: C.white }} onClick={onBack}>UnitScout</div>
          <h1 style={{ ...S.sectionTitle, fontSize: 28, marginBottom: 16 }}>Check your email</h1>
          <p style={{ color: C.grey, fontSize: 15, lineHeight: 1.7, marginBottom: 0 }}>
            If an account exists for <span style={{ color: C.gold }}>{email}</span>, we sent a reset link. Check your inbox.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...S.page, minHeight: "100vh" }}>
      <div style={{ padding: "20px 24px" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: C.grey, fontSize: 22, cursor: "pointer", padding: 0, lineHeight: 1 }}>←</button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "0 24px" }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: 28, marginBottom: 40, cursor: "pointer", color: C.white }} onClick={onBack}>UnitScout</div>
        <div style={{ width: "100%", maxWidth: 480, boxSizing: "border-box" }}>
          <h1 style={{ ...S.sectionTitle, fontSize: 32, marginBottom: 8 }}>Reset Password</h1>
          <p style={{ color: C.grey, fontSize: 14, marginBottom: 28 }}>Enter your email and we'll send you a reset link.</p>
          <input
            style={{ ...S.input, marginBottom: 16 }}
            type="email" placeholder="Email:" autoFocus
            value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleReset()}
          />
          {error && <p style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{error}</p>}
          <button onClick={handleReset} disabled={loading}
            style={{ ...S.goldBtn, width: "100%", fontSize: 16, opacity: loading ? 0.6 : 1 }}>
            {loading ? "Sending..." : "Send Reset Link"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -- Reset Password Page (opened from email link) -------------------------------
function ResetPasswordPage({ onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSave = async () => {
    if (!password || !confirm) { setError("Please fill in both fields."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (!/[0-9]/.test(password)) { setError("Password must include at least one number."); return; }
    if (!/[!@#$%^&*]/.test(password)) { setError("Must include at least one special character (!@#$%^&*)."); return; }
    setLoading(true); setError("");
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) { setError(updateError.message); setLoading(false); return; }
    setDone(true); setLoading(false);
  };

  if (done) {
    return (
      <div style={{ ...S.page, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div style={{ width: "100%", maxWidth: 480, padding: "0 24px", boxSizing: "border-box", textAlign: "center" }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: 28, marginBottom: 32, color: C.white }}>UnitScout</div>
          <h1 style={{ ...S.sectionTitle, fontSize: 28, marginBottom: 12 }}>Password updated</h1>
          <p style={{ color: C.grey, fontSize: 14, marginBottom: 32 }}>Your password has been changed. You can now log in.</p>
          <button onClick={onDone} style={{ ...S.goldBtn, fontSize: 16, padding: "14px 40px" }}>Go to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...S.page, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <div style={{ width: "100%", maxWidth: 480, padding: "0 24px", boxSizing: "border-box" }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: 28, marginBottom: 40, textAlign: "center", color: C.white }}>UnitScout</div>
        <h1 style={{ ...S.sectionTitle, fontSize: 32, marginBottom: 8 }}>New Password</h1>
        <p style={{ color: C.grey, fontSize: 14, marginBottom: 28 }}>Choose a strong password for your account.</p>
        <input style={{ ...S.input, marginBottom: 8 }} type="password" placeholder="New password:" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
        <p style={{ color: C.grey, fontSize: 11, marginBottom: 16, paddingLeft: 4 }}>8+ characters, one number, one special character (!@#$%^&*)</p>
        <input style={{ ...S.input, marginBottom: 24 }} type="password" placeholder="Confirm password:" value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSave()} />
        {error && <p style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{error}</p>}
        <button onClick={handleSave} disabled={loading}
          style={{ ...S.goldBtn, width: "100%", fontSize: 16, opacity: loading ? 0.6 : 1 }}>
          {loading ? "Saving..." : "Save Password"}
        </button>
      </div>
    </div>
  );
}

function ChangeEmailModal({ onClose }) {
  const [newEmail, setNewEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = async () => {
    if (!newEmail) { setError("Please enter a new email."); return; }
    setLoading(true); setError("");
    const { error: updateError } = await supabase.auth.updateUser({ email: newEmail });
    if (updateError) { setError(updateError.message); setLoading(false); return; }
    setSent(true); setLoading(false);
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#000000CC", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ ...S.card, maxWidth: 440, width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, margin: 0 }}>Change Email</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.grey, fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        {sent ? (
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📧</div>
            <p style={{ color: C.white, fontSize: 16, marginBottom: 8 }}>Confirm your new email</p>
            <p style={{ color: C.grey, fontSize: 14, marginBottom: 24 }}>Check <span style={{ color: C.gold }}>{newEmail}</span> and click the link.</p>
            <button onClick={onClose} style={{ ...S.goldBtn, width: "100%" }}>Done</button>
          </div>
        ) : (
          <>
            <input style={{ ...S.input, marginBottom: 16 }} type="email" placeholder="New email address" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            {error && <p style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{error}</p>}
            <button onClick={handleChange} disabled={loading} style={{ ...S.goldBtn, width: "100%", opacity: loading ? 0.6 : 1 }}>
              {loading ? "Updating..." : "Update Email"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = async () => {
    if (!newPassword || !confirmPassword) { setError("Please fill in both fields."); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
    if (newPassword.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (!/[0-9]/.test(newPassword)) { setError("Password must include at least one number."); return; }
    if (!/[!@#$%^&*]/.test(newPassword)) { setError("Must include at least one special character (!@#$%^&*)."); return; }
    setLoading(true); setError("");
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    if (updateError) { setError(updateError.message); setLoading(false); return; }
    setDone(true); setLoading(false);
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#000000CC", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ ...S.card, maxWidth: 440, width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, margin: 0 }}>Change Password</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.grey, fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        {done ? (
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <p style={{ color: C.white, fontSize: 16, marginBottom: 16 }}>Password updated!</p>
            <button onClick={onClose} style={{ ...S.goldBtn, width: "100%" }}>Done</button>
          </div>
        ) : (
          <>
            <p style={{ color: C.grey, fontSize: 12, marginBottom: 16 }}>8+ chars, one number, one special character (!@#$%^&*)</p>
            <input style={{ ...S.input, marginBottom: 12 }} type="password" placeholder="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            <input style={{ ...S.input, marginBottom: 16 }} type="password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            {error && <p style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{error}</p>}
            <button onClick={handleChange} disabled={loading} style={{ ...S.goldBtn, width: "100%", opacity: loading ? 0.6 : 1 }}>
              {loading ? "Updating..." : "Update Password"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// -- Settings Page -------------------------------------------------------------
function SettingsPage({ user, onLogout, onUserUpdate, onShowTerms, totalScanCount, setTotalScanCount }) {
  const plan = PLANS[user?.plan] || PLANS.casual;
  const [scanCount, setScanCount] = useState(0);
  const [totalProfit, setTotalProfit] = useState(null);
  const [memberSince, setMemberSince] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [newUrlLabel, setNewUrlLabel] = useState("");
  const [newUrlValue, setNewUrlValue] = useState("");
  const [savingUrls, setSavingUrls] = useState(false);
  const [isOnlineBidder, setIsOnlineBidder] = useState(user?.isOnlineBidder || false);
  // Confirmation dialogs
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDeleteAccount, setConfirmDeleteAccount] = useState(false);
  const [confirmDeleteUnits, setConfirmDeleteUnits] = useState(false);
  const [deleteAccountInput, setDeleteAccountInput] = useState("");
  const [deleteUnitsInput, setDeleteUnitsInput] = useState("");

  useEffect(() => {
    const loadData = async () => {
      if (!user?.id) return;
      const { data: profile } = await supabase.from("profiles").select("*").eq("id", user.id).single();
      if (profile) {
        let periodEnd = profile.current_period_end;
        // Backfill: free users created before current_period_end was saved
        if (!periodEnd && profile.plan === "free") {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          const signupDate = authUser?.created_at ? new Date(authUser.created_at) : new Date();
          periodEnd = new Date(signupDate.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
          await supabase.from("profiles").update({ current_period_end: periodEnd }).eq("id", user.id);
        }
        onUserUpdate({ ...user, plan: profile.plan, scansRemaining: profile.scans_remaining, currentPeriodEnd: periodEnd, cancelAtPeriodEnd: profile.cancel_at_period_end, isOnlineBidder: !!profile.is_online_bidder });
        setCustomUrls(profile.custom_market_urls || []);
        setIsOnlineBidder(!!profile.is_online_bidder);
      }
      const { count } = await supabase.from("logs").select("*", { count: "exact", head: true }).eq("user_id", user.id);
      if (setTotalScanCount) setTotalScanCount(count || 0);
      // Member since - pull directly from auth, most reliable source
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser?.created_at) setMemberSince(new Date(authUser.created_at));
      else if (profile?.created_at) setMemberSince(new Date(profile.created_at));
      // Total profit from completed logs
      const { data: completedLogs } = await supabase.from("logs").select("total_recovered, winning_bid").eq("user_id", user.id).eq("phase_complete", true);
      if (completedLogs?.length) {
        const profit = completedLogs.reduce((sum, o) => sum + ((o.total_recovered || 0) - (o.winning_bid || 0)), 0);
        setTotalProfit(profit);
      }
    };
    loadData();
  }, []);

  const saveCustomUrls = async (urls) => {
    setSavingUrls(true);
    await supabase.from("profiles").update({ custom_market_urls: urls }).eq("id", user.id);
    setSavingUrls(false);
  };

  const addCustomUrl = async () => {
    if (!newUrlLabel.trim() || !newUrlValue.trim()) return;
    const updated = [...customUrls, { label: newUrlLabel.trim(), url: newUrlValue.trim() }];
    setCustomUrls(updated);
    setNewUrlLabel(""); setNewUrlValue("");
    await saveCustomUrls(updated);
  };

  const removeCustomUrl = async (index) => {
    const updated = customUrls.filter((_, i) => i !== index);
    setCustomUrls(updated);
    await saveCustomUrls(updated);
  };

  const handleLogout = async () => { await supabase.auth.signOut(); onLogout(); };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      // 1. Cancel Stripe subscription if active
      const { data: profile } = await supabase.from("profiles").select("stripe_subscription_id").eq("id", user.id).single();
      if (profile?.stripe_subscription_id) {
        await fetch("/api/cancel-subscription", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ subscriptionId: profile.stripe_subscription_id }),
        });
      }

      // 2. Delete all photos from storage bucket (userId/* folder)
      try {
        const { data: fileList } = await supabase.storage.from("scan-photos").list(user.id, { limit: 1000 });
        if (fileList?.length) {
          // List files in each scan subfolder
          const allPaths = [];
          for (const folder of fileList) {
            const { data: subFiles } = await supabase.storage.from("scan-photos").list(`${user.id}/${folder.name}`, { limit: 100 });
            if (subFiles?.length) {
              subFiles.forEach(f => allPaths.push(`${user.id}/${folder.name}/${f.name}`));
            }
          }
          if (allPaths.length) {
            await supabase.storage.from("scan-photos").remove(allPaths);
          }
        }
      } catch (storageErr) { console.error("Storage cleanup error:", storageErr); }

      // 3. Null out user_id on logs — sever identity, keep rows for data integrity
      await supabase.from("logs").update({ user_id: null }).eq("user_id", user.id);

      // 4. Delete profiles row
      await supabase.from("profiles").delete().eq("id", user.id);

      // 5. Delete Supabase auth user (via API route which uses service role key)
      await fetch("/api/delete-account", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id }),
      });

      await supabase.auth.signOut();
      onLogout();
    } catch (err) {
      console.error("Deletion error:", err);
      setDeleting(false);
    }
  };

  const handleDeleteUnits = async () => {
    await supabase.from("logs").delete().eq("user_id", user.id);
    await supabase.from("profiles").update({ unit_counter: 0 }).eq("id", user.id);
    if (setTotalScanCount) setTotalScanCount(0);
    setTotalProfit(null);
    setConfirmDeleteUnits(false);
    setDeleteUnitsInput("");
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    const { data: profile } = await supabase.from("profiles").select("stripe_customer_id").eq("id", user.id).single();
    if (!profile?.stripe_customer_id) { alert("No subscription found. Please contact support@myunitscout.com"); setCancelLoading(false); return; }
    await redirectToPortal(profile.stripe_customer_id);
    setCancelLoading(false);
  };

  const isCancelled = user?.plan === "cancelled";
  const isPendingCancel = user?.cancelAtPeriodEnd && !isCancelled;
  const formatDate = (dateStr) => !dateStr ? null : new Date(dateStr).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const formatMonthYear = (date) => !date ? null : date.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const isTrialUser = user?.plan === "free";
  const trialEnd = isTrialUser && user?.currentPeriodEnd ? new Date(user.currentPeriodEnd) : null;
  const now = new Date();
  const trialExpired = trialEnd ? now > trialEnd : false;
  const trialDaysLeft = trialEnd && !trialExpired ? Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)) : 0;
  const trialExpiringSoon = !trialExpired && trialDaysLeft <= 7;

  // Confirmation modal component
  const ConfirmTypeModal = ({ title, warning, onClose, onConfirm, confirmWord, inputValue, setInputValue, loading }) => (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#000000CC", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ ...S.card, maxWidth: 400, width: "100%" }}>
        <h3 style={{ color: C.white, margin: "0 0 12px", fontSize: 17 }}>{title}</h3>
        <p style={{ color: C.grey, fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>{warning}</p>
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: C.grey, fontSize: 12, marginBottom: 8 }}>Type <strong style={{ color: C.white }}>{confirmWord}</strong> to confirm</div>
          <input
            autoFocus
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            placeholder={confirmWord}
            style={{ ...S.input, width: "100%", fontSize: 15 }}
          />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ ...S.outlineBtn, flex: 1 }}>Cancel</button>
          <button
            onClick={onConfirm}
            disabled={inputValue !== confirmWord || loading}
            style={{ ...S.redBtn, flex: 1, opacity: inputValue !== confirmWord || loading ? 0.4 : 1 }}>
            {loading ? "Working..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {showChangeEmail && <ChangeEmailModal onClose={() => setShowChangeEmail(false)} />}
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}

      {/* Cancel subscription — 2-step */}
      {confirmCancel && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#000000CC", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ ...S.card, maxWidth: 400, width: "100%" }}>
            <h3 style={{ color: C.white, margin: "0 0 12px", fontSize: 17 }}>Cancel Subscription?</h3>
            <p style={{ color: C.grey, fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
              You'll keep access until the end of your current billing period. After that, your account will be deactivated. This will open the subscription management portal.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmCancel(false)} style={{ ...S.outlineBtn, flex: 1 }}>Keep Subscription</button>
              <button onClick={() => { setConfirmCancel(false); handleCancelSubscription(); }} disabled={cancelLoading}
                style={{ ...S.redBtn, flex: 1, opacity: cancelLoading ? 0.6 : 1 }}>
                {cancelLoading ? "Opening..." : "Yes, Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete account — type DELETE */}
      {confirmDeleteAccount && (
        <ConfirmTypeModal
          title="Delete Account & All Data?"
          warning="This permanently deletes your account, all unit history, photos, and cancels your subscription. This cannot be undone."
          onClose={() => { setConfirmDeleteAccount(false); setDeleteAccountInput(""); }}
          onConfirm={handleDeleteAccount}
          confirmWord="DELETE"
          inputValue={deleteAccountInput}
          setInputValue={setDeleteAccountInput}
          loading={deleting}
        />
      )}

      {/* Delete all units — type DELETE */}
      {confirmDeleteUnits && (
        <ConfirmTypeModal
          title="Delete All Units?"
          warning="This permanently deletes all your unit history and photos. Your account stays active. This cannot be undone."
          onClose={() => { setConfirmDeleteUnits(false); setDeleteUnitsInput(""); }}
          onConfirm={handleDeleteUnits}
          confirmWord="DELETE"
          inputValue={deleteUnitsInput}
          setInputValue={setDeleteUnitsInput}
          loading={false}
        />
      )}

      <div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 24, marginTop: 16 }}>
          <button onClick={handleLogout} style={{ ...S.outlineBtn }}>Log Out</button>
        </div>

        {/* Account */}
        <div style={{ ...S.card, marginBottom: 20 }}>
          <h3 style={{ color: C.gold, margin: "0 0 20px", fontSize: 16 }}>Account</h3>
          <div style={{ marginBottom: 16, display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div>
              <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Email</div>
              <div style={{ color: C.white, fontSize: 15 }}>{user?.email}</div>
            </div>
            {memberSince && (
              <div>
                <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Member Since</div>
                <div style={{ color: C.white, fontSize: 15 }}>{formatMonthYear(memberSince)}</div>
              </div>
            )}
          </div>
          {isTrialUser && memberSince && !trialExpired && (
            <div style={{ marginBottom: 16, display: "flex", gap: 24 }}>
              <div>
                <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Days left on free trial</div>
                <div style={{ color: trialExpiringSoon ? C.gold : C.white, fontSize: 15, fontWeight: 700 }}>{trialDaysLeft}</div>
              </div>
              <div>
                <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Trial expires</div>
                <div style={{ color: trialExpiringSoon ? C.gold : C.white, fontSize: 15 }}>{formatDate(user.currentPeriodEnd)}</div>
              </div>
            </div>
          )}
          {isTrialUser && trialExpired && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Free trial</div>
              <div style={{ color: C.red, fontSize: 15, fontWeight: 600 }}>Expired</div>
            </div>
          )}
          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={() => setShowChangeEmail(true)} style={{ ...S.outlineBtn, fontSize: 13 }}>Change Email</button>
            <button onClick={() => setShowChangePassword(true)} style={{ ...S.outlineBtn, fontSize: 13 }}>Change Password</button>
          </div>
        </div>

        {/* Subscription */}
        <div style={{ ...S.card, marginBottom: 20 }}>
          <h3 style={{ color: C.gold, margin: "0 0 20px", fontSize: 16 }}>Subscription</h3>
          <div style={{ marginBottom: 20, display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div>
              <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Current Plan</div>
              <div style={{ color: isCancelled ? C.red : isPendingCancel ? C.yellow : C.white, fontSize: 18, fontWeight: 700 }}>
                {isCancelled ? "Cancelled" : isTrialUser ? "UnitScout+ Trial" : "UnitScout+"}{isPendingCancel ? " (Cancelling)" : ""}
              </div>
            </div>
            {!isCancelled && user?.currentPeriodEnd && !isTrialUser && (
              <div>
                <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>{isPendingCancel ? "Access until" : "Next Billing Date"}</div>
                <div style={{ color: isPendingCancel ? C.red : C.white, fontSize: 15, fontWeight: 600 }}>{formatDate(user.currentPeriodEnd)}</div>
              </div>
            )}
          </div>

          {/* Trial states */}
          {isTrialUser && !trialExpired && trialEnd && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Trial expires</div>
              <div style={{ color: trialExpiringSoon ? C.gold : C.white, fontSize: 14, fontWeight: 600 }}>
                {formatDate(user.currentPeriodEnd)}
                {trialExpiringSoon && <span style={{ color: C.gold, fontSize: 12, fontWeight: 400, marginLeft: 8 }}>{trialDaysLeft === 1 ? "1 day left" : `${trialDaysLeft} days left`}</span>}
              </div>
              <div style={{ color: C.grey, fontSize: 13, marginTop: 10, lineHeight: 1.6 }}>
                After your trial ends, you'll need UnitScout+ to keep logging units. Your existing data is always saved.
              </div>
            </div>
          )}
          {isTrialUser && trialExpired && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: C.red, fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Your trial has ended.</div>
              <div style={{ color: C.grey, fontSize: 13, lineHeight: 1.6 }}>
                Upgrade to keep logging units. Your data is safe and waiting.
              </div>
            </div>
          )}

          {/* CTAs */}
          {isTrialUser && (
            <button onClick={() => redirectToCheckout("pro", user.id, user.email)} style={{ ...S.goldBtn, fontSize: 13, padding: "10px 20px" }}>
              Upgrade to UnitScout+ — $5.99/mo
            </button>
          )}
          {!isCancelled && !isPendingCancel && !isTrialUser && (
            <button onClick={() => setConfirmCancel(true)} disabled={cancelLoading} style={{ ...S.redBtn, opacity: cancelLoading ? 0.6 : 1 }}>
              Cancel Subscription
            </button>
          )}
          {isCancelled && (
            <button onClick={() => redirectToCheckout("pro", user.id, user.email, null)} style={{ ...S.goldBtn, fontSize: 13, padding: "10px 20px" }}>
              Resubscribe
            </button>
          )}
        </div>

        {/* Preferences */}
        <div style={{ ...S.card, marginBottom: 20 }}>
          <h3 style={{ color: C.gold, margin: "0 0 16px", fontSize: 16 }}>Preferences</h3>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div>
              <div style={{ color: C.white, fontSize: 14, fontWeight: 600 }}>Online Bidder</div>
              <div style={{ color: C.grey, fontSize: 12, marginTop: 2 }}>Switch to online bidder mode. Install the Chrome extension for the best experience.</div>
            </div>
            <div
              onClick={async () => {
                const next = !isOnlineBidder;
                setIsOnlineBidder(next);
                await supabase.from("profiles").update({ is_online_bidder: next }).eq("id", user.id);
                onUserUpdate({ ...user, isOnlineBidder: next });
              }}
              style={{
                width: 44, height: 24, borderRadius: 12, cursor: "pointer", flexShrink: 0,
                background: isOnlineBidder ? C.gold : C.border,
                position: "relative", transition: "background 0.2s",
              }}>
              <div style={{
                position: "absolute", top: 2, left: isOnlineBidder ? 22 : 2,
                width: 20, height: 20, borderRadius: "50%", background: C.white,
                transition: "left 0.2s", boxShadow: "0 1px 3px #0005",
              }} />
            </div>
          </div>
        </div>

        {/* Your Data */}
        <div style={{ ...S.card, marginBottom: 20 }}>
          <h3 style={{ color: C.gold, margin: "0 0 16px", fontSize: 16 }}>Your Data</h3>
          {totalProfit !== null && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: C.grey, fontSize: 12, marginBottom: 4 }}>Total profit generated</div>
              <div style={{ color: totalProfit >= 0 ? C.green : C.red, fontSize: 20, fontWeight: 700 }}>${totalProfit.toLocaleString()}</div>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 48, paddingTop: 16 }}>
            <div>
              <button onClick={() => setConfirmDeleteAccount(true)} disabled={deleting} style={{ ...S.redBtn, opacity: deleting ? 0.6 : 1 }}>
                Delete My Account & Data
              </button>
              <div style={{ color: C.grey, fontSize: 12, marginTop: 6 }}>Permanently deletes your account and all data.</div>
            </div>
            <div>
              <button onClick={() => setConfirmDeleteUnits(true)} style={{ ...S.redBtn }}>
                Delete All Units
              </button>
              <div style={{ color: C.grey, fontSize: 12, marginTop: 6 }}>Removes all logged units. Your account stays active.</div>
            </div>
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 32, paddingTop: 24, textAlign: "center", display: "flex", justifyContent: "center", gap: 16 }}>
          <span onClick={() => setShowTerms(true)} style={{ color: C.grey, fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>Terms of Service</span>
          <span style={{ color: C.border }}>·</span>
          <span onClick={() => setShowPrivacy(true)} style={{ color: C.grey, fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>Privacy Policy</span>
        </div>
        {showTerms && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#000000EE", zIndex: 1000, overflowY: "auto", padding: 24 }}>
            <div style={{ maxWidth: 800, margin: "0 auto", paddingTop: 24 }}>
              <button onClick={() => setShowTerms(false)} style={{ ...S.outlineBtn, marginBottom: 32 }}>← Back</button>
              <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 36, marginBottom: 8 }}>Terms of Service</h1>
              <p style={{ color: C.grey, marginBottom: 32 }}>Last updated: March 2026</p>
              <div style={{ color: C.grey, lineHeight: 1.8, fontSize: 14 }}>
                <p>By using UnitScout, you agree to these terms. UnitScout identifies visible items in storage unit photos and provides links to eBay sold listings. We do not generate price estimates or appraisals. All valuations are the user's responsibility based on external research.</p>
                <p>You agree not to rely solely on UnitScout when making bidding decisions. UnitScout is not responsible for any financial losses resulting from bids placed.</p>
                <p>Subscriptions are billed monthly and can be cancelled at any time. No refunds for partial months.</p>
                <p>You retain ownership of all photos you upload. By uploading, you grant UnitScout a license to process these images to provide our service.</p>
                <p>We reserve the right to terminate accounts that violate these terms or misuse our service.</p>
              </div>
            </div>
          </div>
        )}
        {showPrivacy && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#000000EE", zIndex: 1000, overflowY: "auto", padding: 24 }}>
            <div style={{ maxWidth: 800, margin: "0 auto", paddingTop: 24 }}>
              <button onClick={() => setShowPrivacy(false)} style={{ ...S.outlineBtn, marginBottom: 32 }}>← Back</button>
              <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 36, marginBottom: 8 }}>Privacy Policy</h1>
              <p style={{ color: C.grey, marginBottom: 32 }}>Last updated: March 2026</p>
              <div style={{ color: C.grey, lineHeight: 1.8, fontSize: 14 }}>
                <p>UnitScout collects the information you provide when creating an account (email address) and when using the app (photos, outcome data, bid amounts). We use this data solely to provide and improve the UnitScout service.</p>
                <p>Photos you upload are processed by AI to identify items. Images are stored securely and are only accessible to you. We do not share or sell your photos or personal data to third parties.</p>
                <p>We use Supabase for data storage and Stripe for payment processing. These services have their own privacy policies. We do not store payment card details — all payment data is handled by Stripe.</p>
                <p>You can delete your account and all associated data at any time from the Settings page. Deletion is permanent and irreversible.</p>
                <p>We may update this policy as the product evolves. Continued use of UnitScout after changes constitutes acceptance of the updated policy. Questions? Contact us at support@myunitscout.com.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// -- App Shell -----------------------------------------------------------------
function AppShell({ user, currentTab, setCurrentTab, children, hasPending }) {
  const tabs = [
    { id: "scanner", label: "Log Unit" },
    { id: "history", label: "History" },
    { id: "insights", label: "Insights" },
    { id: "settings", label: "Settings" },
  ];
  return (
    <div style={{ background: C.bg, minHeight: "100vh", overflowX: "hidden" }}>
      <nav style={{ background: C.bg, borderBottom: `1px solid ${C.border}`, padding: "0 8px", display: "flex", alignItems: "center", height: 56, position: "fixed", top: 0, left: 0, right: 0, zIndex: 200, boxSizing: "border-box", overflow: "hidden" }}>
        <div className="nav-logo" style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", color: C.white, flexShrink: 0, whiteSpace: "nowrap" }}>UnitScout</div>
        <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <div style={{ display: "flex", gap: 0 }}>
            {tabs.map((tab) => {
              const isActive = currentTab === tab.id;
              return (
                <button key={tab.id} onClick={() => setCurrentTab(tab.id)} className="nav-tab" style={{
                  background: "none", border: "none",
                  borderBottom: isActive ? `2px solid ${C.gold}` : "2px solid transparent",
                  height: 56,
                  color: isActive ? C.gold : C.grey,
                  fontWeight: isActive ? 600 : 400, cursor: "pointer",
                  transition: "color 0.15s", whiteSpace: "nowrap",
                }}>
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ width: 88, flexShrink: 0 }} />
      </nav>
      {/* gap-killer: same bg fills the 56px nav offset so no stripe shows on scroll */}
      <div style={{ height: 56, background: C.bg }} />
      <div style={{ width: "100%", maxWidth: 1400, margin: "0 auto", padding: "0 20px 48px", boxSizing: "border-box" }}>
        {children}
      </div>
    </div>
  );
}

// -- Success Page --------------------------------------------------------------
function SuccessPage({ onEnterApp, paymentType }) {
  const title = paymentType === "upgrade" ? "Your Plan Has Been Upgraded!"
    : paymentType === "downgrade" ? "Your Plan Has Been Downgraded!"
    : "Welcome to UnitScout!";
  return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <div style={{ textAlign: "center", padding: "0 24px", maxWidth: 560 }}>
        <div style={{ fontSize: 64, marginBottom: 24 }}>🎉</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: 42, color: C.white, margin: "0 0 20px" }}>{title}</h1>
        <p style={{ color: C.grey, fontSize: 18, lineHeight: 1.7, maxWidth: 460, margin: "0 auto 36px" }}>
          Upload photos, see what's inside, check eBay — and start building your personal auction intelligence.
        </p>
        <button onClick={onEnterApp} style={{ ...S.goldBtn, fontSize: 18, padding: "16px 48px" }}>Start Scanning →</button>
      </div>
    </div>
  );
}

// -- Terms Page ----------------------------------------------------------------
function TermsPage({ onBack }) {
  return (
    <div style={S.page}>
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 24px" }}>
        <button onClick={onBack} style={{ ...S.outlineBtn, marginBottom: 32 }}>← Back</button>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 36, marginBottom: 8 }}>Terms of Service</h1>
        <p style={{ color: C.grey, marginBottom: 32 }}>Last updated: March 2026</p>
        <div style={{ color: C.grey, lineHeight: 1.8, fontSize: 14 }}>
          <p>By using UnitScout, you agree to these terms. UnitScout identifies visible items in storage unit photos and provides links to eBay sold listings for reference. We do not generate price estimates or appraisals of any kind.</p>
          <p>You agree not to rely solely on UnitScout when making bidding decisions. UnitScout is not responsible for any financial losses resulting from bids placed.</p>
          <p>Subscriptions are billed monthly and can be cancelled at any time. No refunds for partial months.</p>
          <p>You retain ownership of all photos you upload. By uploading, you grant UnitScout a license to process these images to provide our service.</p>
          <p>We reserve the right to terminate accounts that violate these terms or misuse our service.</p>
        </div>
      </div>
    </div>
  );
}

// -- Landing Page --------------------------------------------------------------
function LandingPage({ onLogin, onGetStarted, onShowTerms }) {
  const [hoveredPaid, setHoveredPaid] = useState(false);

  return (
    <div style={S.page}>
      {/* Nav */}
      <nav style={{ background: C.bg, borderBottom: `1px solid ${C.border}`, padding: "0 32px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 60 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: 22, color: C.white }}>UnitScout</div>
        <button onClick={onLogin} style={{ ...S.outlineBtn, padding: "8px 20px", fontSize: 14 }}>Log In</button>
      </nav>

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 24px 80px" }}>

        {/* Hero */}
        <div style={{ textAlign: "center", padding: "100px 0 80px" }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 68, fontWeight: 700, margin: "0 0 24px", lineHeight: 1.08, color: C.white }}>
            UnitScout
          </h1>
          <p style={{ fontSize: 22, color: C.white, margin: "0 0 40px", lineHeight: 1.5, maxWidth: 520, marginLeft: "auto", marginRight: "auto" }}>
            Bid smarter. Track every unit. Know your ROI.
          </p>
          <button onClick={() => onGetStarted("free")}
            style={{ ...S.goldBtn, fontSize: 17, padding: "16px 36px", borderRadius: 12 }}>
            Start Free — No Card Required
          </button>
        </div>

        {/* How It Works */}
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 400, textAlign: "center", margin: "0 0 40px", color: C.white }}>
          How It Works
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 100 }}>
          {[
            { icon: <Camera size={36} color={C.gold} strokeWidth={1.5} />, title: "Log any unit", desc: "Your photo, your outcome, your bid. At the auction or online." },
            { icon: <FolderOpen size={36} color={C.gold} strokeWidth={1.5} />, title: "Your complete auction record", desc: "Every unit you've ever seen. Your photos, your outcomes, your bids. All in one place." },
            { icon: <TrendingUp size={36} color={C.gold} strokeWidth={1.5} />, title: "Track your profits", desc: "Enter what you recovered. See your ROI instantly — unit by unit." },
            { icon: <BarChart2 size={36} color={C.gold} strokeWidth={1.5} />, title: "Know your edge", desc: "Which facilities you bid at perform best. What your average ROI is on every unit you buy. What your average inventory turnaround time is." },
          ].map((step, i) => (
            <div key={i} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: "28px 20px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ marginBottom: 16 }}>{step.icon}</div>
              <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 10, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>{step.title}</div>
              <div style={{ color: C.white, fontSize: 13, lineHeight: 1.6, fontWeight: 400 }}>{step.desc}</div>
            </div>
          ))}
        </div>

        {/* Insights preview */}
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 400, textAlign: "center", margin: "0 0 40px", color: C.white }}>
          Know Where You Win
        </h2>
        <div style={{ textAlign: "center", marginBottom: 100 }}>
          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: "60px 40px", marginBottom: 20 }}>
            <div style={{ color: C.white, fontSize: 15 }}>[ Future Insights Section ]</div>
          </div>
          <p style={{ color: C.white, fontSize: 16 }}>
            Your bidding intelligence grows with every unit you log.
          </p>
        </div>

        {/* Pricing */}
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 400, textAlign: "center", margin: "0 0 40px", color: C.white }}>
          Pricing
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, maxWidth: 680, margin: "0 auto 80px" }}>

          {/* Free trial */}
          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: "32px 28px" }}>
            <div style={{ color: C.white, fontSize: 20, fontWeight: 700, marginBottom: 6 }}>UnitScout+ Trial</div>
            <div style={{ color: C.gold, fontSize: 36, fontWeight: 800, marginBottom: 16, lineHeight: 1 }}>$0</div>
            <div style={{ color: C.white, fontSize: 13, marginBottom: 24 }}>1 month · no card required</div>
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20, marginBottom: 28, display: "flex", flexDirection: "column", gap: 10 }}>
              {["Full access to everything", "No card required", "All data saved when you subscribe"].map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ color: C.gold, fontSize: 14, flexShrink: 0 }}>✓</span>
                  <span style={{ color: C.white, fontSize: 13 }}>{f}</span>
                </div>
              ))}
            </div>
            <button onClick={() => onGetStarted("free")} style={{ ...S.outlineBtn, width: "100%", padding: "13px", fontSize: 15 }}>
              Start Free
            </button>
          </div>

          {/* Paid — direct to Stripe, no trial mention */}
          <div
            onMouseEnter={() => setHoveredPaid(true)}
            onMouseLeave={() => setHoveredPaid(false)}
            style={{
              background: C.bgCard,
              border: `2px solid ${C.gold}`,
              borderRadius: 16, padding: "32px 28px",
              boxShadow: hoveredPaid ? `0 0 40px ${C.gold}40` : `0 0 16px ${C.gold}18`,
              transition: "box-shadow 0.25s ease",
            }}>
            <div style={{ color: C.white, fontSize: 20, fontWeight: 700, marginBottom: 6 }}>UnitScout+</div>
            <div style={{ color: C.gold, fontSize: 36, fontWeight: 800, marginBottom: 16, lineHeight: 1 }}>$5.99</div>
            <div style={{ color: C.white, fontSize: 13, marginBottom: 24 }}>per month · cancel anytime</div>
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20, marginBottom: 28, display: "flex", flexDirection: "column", gap: 10 }}>
              {["Everything in the trial", "Keep all your data forever", "Unlimited history", "Full Insights", "Cancel anytime"].map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ color: C.gold, fontSize: 14, flexShrink: 0 }}>✓</span>
                  <span style={{ color: C.white, fontSize: 13 }}>{f}</span>
                </div>
              ))}
            </div>
            <button onClick={() => onGetStarted("pro")} style={{ ...S.goldBtn, width: "100%", padding: "13px", fontSize: 15 }}>
              Get UnitScout+
            </button>
          </div>
        </div>

        {/* Footer */}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 32, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <span style={{ color: C.grey, fontSize: 12 }}>support@</span>
            <span style={{ color: C.grey, fontSize: 12 }}>partners@</span>
          </div>
          <div style={{ display: "flex", gap: 20 }}>
            <span onClick={onShowTerms} style={{ color: C.grey, fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>Terms of Service</span>
            <span style={{ color: C.grey, fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>Privacy Policy</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// -- Login Page ----------------------------------------------------------------
function LoginPage({ onLogin, onCreateAccount, onBack, onForgotPassword }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");
    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) { setError(authError.message); setLoading(false); return; }
    const { data: profile } = await supabase.from("profiles").select("*").eq("id", data.user.id).single();
    onLogin({
      id: data.user.id, email: data.user.email,
      plan: profile?.plan || "casual",
      scansRemaining: profile?.scans_remaining ?? PLANS[profile?.plan || "casual"].scans,
      currentPeriodEnd: profile?.current_period_end,
      cancelAtPeriodEnd: profile?.cancel_at_period_end,
      isOnlineBidder: !!profile?.is_online_bidder,
    });
    setLoading(false);
  };

  return (
    <div style={{ ...S.page, minHeight: "100vh" }}>
      <div style={{ padding: "20px 32px" }}>
        <button onClick={onBack} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 16px", color: C.grey, fontSize: 14, cursor: "pointer" }}>
          ← Back to Home
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "0 24px 60px" }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: 28, marginBottom: 32, cursor: "pointer" }} onClick={onBack}>UnitScout</div>
        <div style={{ width: "100%", maxWidth: 480, boxSizing: "border-box" }}>
          <h1 style={{ ...S.sectionTitle, fontSize: 36, marginBottom: 32 }}>Welcome Back</h1>
          <input style={{ ...S.input, marginBottom: 16 }} type="email" placeholder="Email:" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input style={{ ...S.input, marginBottom: 8 }} type="password" placeholder="Password:" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleLogin()} />
          <p onClick={onForgotPassword} style={{ textAlign: "right", color: C.gold, fontSize: 13, marginBottom: 20, cursor: "pointer" }}>
            Forgot password?
          </p>
          {error && <p style={{ color: C.red, fontSize: 13, marginBottom: 12, textAlign: "center" }}>{error}</p>}
          <button onClick={handleLogin} disabled={loading} style={{ ...S.goldBtn, width: "100%", fontSize: 16, opacity: loading ? 0.6 : 1 }}>
            {loading ? "Logging in..." : "Log In"}
          </button>
          <p style={{ textAlign: "center", color: C.grey, fontSize: 13, marginTop: 20 }}>
            Don't have an account?{" "}
            <span onClick={() => onCreateAccount(null)} style={{ color: C.gold, cursor: "pointer", fontWeight: 600 }}>Create Account</span>
          </p>
        </div>
      </div>
    </div>
  );
}

// -- Create Account Page -------------------------------------------------------
function CreateAccountPage({ preSelectedPlan, onSuccess, onLogin, onBack }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isFree = preSelectedPlan === "free";

  const handleCreate = async () => {
    if (!email || !password || !confirmPassword) { setError("Please fill in all fields."); return; }
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }
    if (!agreedToTerms) { setError("Please agree to the Terms and Conditions."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (!/[0-9]/.test(password)) { setError("Password must include at least one number."); return; }
    if (!/[!@#$%^&*]/.test(password)) { setError("Must include at least one special character (!@#$%^&*)."); return; }
    setLoading(true); setError("");
    const { data, error: authError } = await supabase.auth.signUp({ email, password });
    if (authError) { setError(authError.message); setLoading(false); return; }
    if (!data.user) { setError("Signup failed. Please try again."); setLoading(false); return; }
    const trialEnd = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    await supabase.auth.signInWithPassword({ email, password });
    if (isFree) {
      // Free plan - no Stripe, set profile and go straight to app
      await supabase.from("profiles").upsert({
        id: data.user.id, email,
        plan: "free",
        scans_remaining: 99999,
        scans_reset_date: trialEnd.toISOString(),
        current_period_end: trialEnd.toISOString(),
      });
      const { data: { session } } = await supabase.auth.getSession();
      const { data: profile } = await supabase.from("profiles").select("*").eq("id", data.user.id).single();
      onSuccess({ id: data.user.id, email, plan: "free", scansRemaining: 99999, currentPeriodEnd: trialEnd.toISOString() });
    } else {
      // Paid plan - create profile then redirect to Stripe
      await supabase.from("profiles").upsert({ id: data.user.id, email, plan: "free", scans_remaining: 99999, scans_reset_date: trialEnd.toISOString(), current_period_end: trialEnd.toISOString() });
      await redirectToCheckout("pro", data.user.id, email);
    }
    setLoading(false);
  };

  if (showTerms) return <TermsPage onBack={() => setShowTerms(false)} />;

  return (
    <div style={{ ...S.page, minHeight: "100vh" }}>
      <div style={{ padding: "20px 32px" }}>
        <button onClick={onBack} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 16px", color: C.grey, fontSize: 14, cursor: "pointer" }}>
          ← Back to Home
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "0 24px 60px" }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: 28, marginBottom: 32, cursor: "pointer" }} onClick={onBack}>UnitScout</div>
        <div style={{ width: "100%", maxWidth: 520, boxSizing: "border-box" }}>
          <h1 style={{ ...S.sectionTitle, fontSize: 36, marginBottom: 8 }}>Create Account</h1>
          <p style={{ color: C.grey, fontSize: 14, marginBottom: 28 }}>
            {isFree ? "Start your free month — no card required." : "Get UnitScout+ for $5.99/month. Cancel anytime."}
          </p>
          <input style={{ ...S.input, marginBottom: 16 }} type="email" placeholder="Email:" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input style={{ ...S.input, marginBottom: 8 }} type="password" placeholder="Password:" value={password} onChange={(e) => setPassword(e.target.value)} />
          <p style={{ color: C.grey, fontSize: 11, marginBottom: 16, paddingLeft: 4 }}>8+ characters, one number, one special character (!@#$%^&*)</p>
          <input style={{ ...S.input, marginBottom: 24 }} type="password" placeholder="Confirm Password:" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          <label style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 24, cursor: "pointer" }}>
            <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} style={{ accentColor: C.gold, width: 16, height: 16, marginTop: 2, flexShrink: 0 }} />
            <span style={{ color: C.grey, fontSize: 13, lineHeight: 1.5 }}>
              I agree to the{" "}
              <span onClick={() => setShowTerms(true)} style={{ color: C.gold, cursor: "pointer", textDecoration: "underline" }}>Terms and Conditions</span>
            </span>
          </label>
          {error && <p style={{ color: C.red, fontSize: 13, marginBottom: 12, textAlign: "center" }}>{error}</p>}
          <button onClick={handleCreate} disabled={!agreedToTerms || loading}
            style={{ ...S.goldBtn, width: "100%", fontSize: 16, opacity: agreedToTerms && !loading ? 1 : 0.4, cursor: agreedToTerms && !loading ? "pointer" : "not-allowed" }}>
            {loading ? "Creating account..." : isFree ? "Start Free Month" : "Create Account & Subscribe →"}
          </button>
          <p style={{ textAlign: "center", color: C.grey, fontSize: 13, marginTop: 20 }}>
            Already have an account?{" "}
            <span onClick={onLogin} style={{ color: C.gold, cursor: "pointer", fontWeight: 600 }}>Log In</span>
          </p>
        </div>
      </div>
    </div>
  );
}

// -- Main App ------------------------------------------------------------------
export default function App() {
  const [page, setPage] = useState("loading");
  const [user, setUser] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currentTab, setCurrentTab] = useState("scanner");
  const [paymentType, setPaymentType] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [hasPending, setHasPending] = useState(false);
  const [totalScanCount, setTotalScanCount] = useState(0);
  const [historyDefaultFilter, setHistoryDefaultFilter] = useState("all");

  useEffect(() => {
    injectFonts();
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get("payment");
    const pType = params.get("type");
    const isReset = params.get("reset") === "true";
    if (pType) setPaymentType(pType);
    if (isReset) { setPage("reset"); window.history.replaceState({}, document.title, window.location.pathname); return; }

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: profile } = await supabase.from("profiles").select("*").eq("id", session.user.id).single();
        setUser({
          id: session.user.id, email: session.user.email,
          plan: profile?.plan || "casual",
          scansRemaining: profile?.scans_remaining ?? 30,
          currentPeriodEnd: profile?.current_period_end,
          cancelAtPeriodEnd: profile?.cancel_at_period_end,
          isOnlineBidder: !!profile?.is_online_bidder,
        });
        setPage(paymentStatus === "success" ? "success" : "app");
      } else {
        setPage(paymentStatus === "success" ? "success" : "landing");
      }
      if (paymentStatus) window.history.replaceState({}, document.title, window.location.pathname);
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") { setPage("reset"); return; }
      if (!session) { setUser(null); setPage((current) => current === "success" ? "success" : "landing"); }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = (userData) => { setUser(userData); setPage("app"); setCurrentTab("scanner"); };
  const handleGetStarted = (plan) => {
    setSelectedPlan(plan);
    setPage("create");
  };
  const handleCreateSuccess = (userData) => { setUser(userData); setPage("app"); setCurrentTab("scanner"); };
  const handleLogout = () => { setUser(null); setPage("landing"); };
  const handleScanComplete = () => {
    setUser((prev) => ({ ...prev, scansRemaining: Math.max(0, (prev?.scansRemaining || 0) - 1) }));
    setTotalScanCount(prev => prev + 1);
  };
  const handleUserUpdate = (updates) => { setUser((prev) => ({ ...prev, ...updates })); };

  if (page === "loading") return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: C.gold, fontSize: 18 }}>Loading...</div>
    </div>
  );
  if (page === "terms") return <TermsPage onBack={() => setPage("landing")} />;
  if (page === "landing") return <LandingPage onLogin={() => setPage("login")} onGetStarted={handleGetStarted} onShowTerms={() => setPage("terms")} />;
  if (page === "login") return <LoginPage onLogin={handleLogin} onCreateAccount={handleGetStarted} onBack={() => setPage("landing")} onForgotPassword={() => setPage("forgot")} />;
  if (page === "forgot") return <ForgotPasswordPage onBack={() => setPage("login")} />;
  if (page === "reset") return <ResetPasswordPage onDone={() => setPage("login")} />;
  if (page === "create") return <CreateAccountPage preSelectedPlan={selectedPlan} onSuccess={handleCreateSuccess} onLogin={() => setPage("login")} onBack={() => setPage("landing")} />;
  if (page === "success") return <SuccessPage paymentType={paymentType} onEnterApp={() => { if (user) { setPage("app"); setCurrentTab("scanner"); } else { setPage("login"); } }} />;

  if (page === "app") {
    return (
      <AppShell user={user} currentTab={currentTab} setCurrentTab={setCurrentTab} hasPending={hasPending}>
        {currentTab === "scanner" && <ScannerPage user={user} onScanComplete={handleScanComplete} onGoToSettings={() => setCurrentTab("settings")} />}
        {currentTab === "history" && <HistoryPage user={user} onPendingChange={setHasPending} onChangePage={setCurrentTab} defaultFilter={historyDefaultFilter} onFilterConsumed={() => setHistoryDefaultFilter("all")} />}
        {currentTab === "insights" && (
          <InsightsPage user={user} onChangePlan={() => setShowUpgradeModal(true)} showUpgradeModal={showUpgradeModal} setShowUpgradeModal={setShowUpgradeModal} onGoToHistory={(filter) => { if (filter) setHistoryDefaultFilter(filter); setCurrentTab("history"); }} />
        )}
        {currentTab === "settings" && (
          <SettingsPage key="settings" user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} onShowTerms={() => setPage("terms")} totalScanCount={totalScanCount} setTotalScanCount={setTotalScanCount} />
        )}
      </AppShell>
    );
  }
  return null;
}
